export const WAEC_PHYSICS_LEARN_SS2 = [
  {
    topic: 'Scalars and Vectors',
    topicCode: 'SS2-PHY-01',
    module: 'Mechanics',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro">Physical quantities are <span class="learn-keyword">scalars</span> (magnitude only) or <span class="learn-keyword">vectors</span> (magnitude + direction). Vectors are added by triangle or parallelogram law and resolved into perpendicular components.</div><h3 class="learn-subheading">Key Concepts</h3><ul class="learn-list"><li>Scalars: mass, temperature, time, speed, energy, distance</li><li>Vectors: displacement, velocity, force, acceleration, momentum</li><li>Resultant of perpendicular vectors: R = √(A²+B²); θ = arctan(B/A)</li><li>Components: Fₓ = Fcosθ; Fᵧ = Fsinθ</li><li>Relative velocity: V_AB = V_A − V_B</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Scalar = magnitude only. Vector = magnitude + direction. R = √(A²+B²) for perpendicular. Fₓ=Fcosθ; Fᵧ=Fsinθ. Triangle law: head to tail. Parallelogram: diagonal = resultant.</div>',
    questions: [
      {
        q: 'Which is a scalar quantity?',
        o: ['Velocity', 'Force', 'Temperature', 'Displacement'],
        a: 2,
        e: 'Temperature has magnitude only — no direction. Velocity, force, and displacement all have both magnitude and direction (vectors).',
        h: 'Scalar = magnitude only: temperature, mass, speed, time.',
        yr: 'SS2',
      },
      {
        q: 'Which is a vector quantity?',
        o: ['Mass', 'Speed', 'Time', 'Acceleration'],
        a: 3,
        e: 'Acceleration has magnitude (m/s²) and direction — it is a vector. Mass, speed, and time are scalars.',
        h: 'Vector = magnitude + direction: acceleration, velocity, force.',
        yr: 'SS2',
      },
      {
        q: 'Two forces 3 N and 4 N at right angles. Resultant?',
        o: ['7 N', '1 N', '5 N', '12 N'],
        a: 2,
        e: 'R = √(3²+4²) = √25 = 5 N (3-4-5 right triangle).',
        h: 'R = √(A²+B²) = √(9+16) = 5 N.',
        yr: 'SS2',
      },
      {
        q: 'Horizontal component of force F at angle θ to horizontal is:',
        o: ['F sinθ', 'F cosθ', 'F tanθ', 'F/cosθ'],
        a: 1,
        e: 'Horizontal = F cosθ; Vertical = F sinθ.',
        h: 'Horizontal = F cosθ. Vertical = F sinθ.',
        yr: 'SS2',
      },
      {
        q: 'Parallelogram law: two vectors tail to tail, their resultant is:',
        o: [
          'Sum of their magnitudes',
          'The diagonal of the parallelogram they form',
          'Their difference',
          'The shorter vector',
        ],
        a: 1,
        e: 'Parallelogram law: place vectors tail to tail; the diagonal from their common point is the resultant.',
        h: 'Parallelogram law: resultant = diagonal from common tail point.',
        yr: 'SS2',
      },
      {
        q: 'Force of 10 N at 60° to horizontal. Vertical component?',
        o: ['5 N', '8.66 N', '10 N', '5.77 N'],
        a: 1,
        e: 'Fᵧ = F sinθ = 10×sin60° = 10×0.866 = 8.66 N.',
        h: 'Fᵧ = 10 sin60° = 8.66 N.',
        yr: 'SS2',
      },
      {
        q: 'Triangle law: vectors placed head to tail; resultant is:',
        o: [
          'Line from first head to last tail',
          'Line from first tail to last head',
          'Sum of all magnitudes',
          'Perpendicular to the first vector',
        ],
        a: 1,
        e: 'Triangle law: head to tail arrangement; resultant = from tail of first to head of last.',
        h: 'Triangle law: head-to-tail; resultant closes triangle from first tail to last head.',
        yr: 'SS2',
      },
      {
        q: 'Car A: 60 km/h north. Car B: 100 km/h north. Velocity of A relative to B?',
        o: ['160 km/h north', '40 km/h south', '40 km/h north', '160 km/h south'],
        a: 1,
        e: 'V_AB = V_A − V_B = 60−100 = −40 km/h north = 40 km/h south.',
        h: 'V_AB = V_A − V_B = 60−100 = −40 (40 km/h south).',
        yr: 'SS2',
      },
      {
        q: 'Which pair: one scalar and one vector?',
        o: [
          'Distance and speed',
          'Velocity and acceleration',
          'Force and momentum',
          'Distance and displacement',
        ],
        a: 3,
        e: 'Distance = scalar; displacement = vector. The classic scalar-vector pair.',
        h: 'Distance (scalar) and displacement (vector).',
        yr: 'SS2',
      },
      {
        q: 'Two vectors are equal if they have:',
        o: [
          'Same magnitude only',
          'Same direction only',
          'Same magnitude AND same direction',
          'Same starting point',
        ],
        a: 2,
        e: 'Equal vectors must have identical magnitude AND identical direction.',
        h: 'Equal vectors: same magnitude AND same direction.',
        yr: 'SS2',
      },
      {
        q: 'Force 10 N at 30° to horizontal. Horizontal component?',
        o: ['5 N', '8.66 N', '10 N', '3.33 N'],
        a: 1,
        e: 'Fₓ = F cos30° = 10×0.866 = 8.66 N.',
        h: 'Fₓ = 10 cos30° = 8.66 N.',
        yr: 'SS2',
      },
      {
        q: 'Two equal forces F at 90° to each other. Resultant?',
        o: ['2F', 'F√2', 'F/√2', 'Zero'],
        a: 1,
        e: 'R = √(F²+F²) = F√2.',
        h: 'Two equal forces at 90°: R = F√2.',
        yr: 'SS2',
      },
      {
        q: 'Speed vs velocity: correct distinction?',
        o: [
          'Speed is a vector; velocity is scalar',
          'Velocity = speed with direction; speed = magnitude of velocity',
          'Speed is always greater',
          'Velocity has no unit',
        ],
        a: 1,
        e: 'Velocity = speed + direction (vector). Speed = magnitude of velocity (scalar).',
        h: 'Velocity = speed + direction (vector). Speed = magnitude of velocity (scalar).',
        yr: 'SS2',
      },
      {
        q: '5 N, 12 N, 13 N forces. If 13 N is the resultant of the others, angle between 5 N and 12 N?',
        o: ['30°', '45°', '60°', '90°'],
        a: 3,
        e: '√(5²+12²) = √169 = 13 N. 5-12-13 right triangle → angle = 90°.',
        h: '5-12-13 Pythagorean triple: angle between 5 N and 12 N = 90°.',
        yr: 'SS2',
      },
      {
        q: 'Most systematic method for many vectors?',
        o: [
          'Measure longest',
          'Component method: resolve all into x and y, sum separately, then combine',
          'Multiply magnitudes',
          'Add same-direction vectors only',
        ],
        a: 1,
        e: 'Component method: resolve all → ΣFₓ, ΣFᵧ → R = √(ΣFₓ²+ΣFᵧ²).',
        h: 'Component method: resolve → sum x and y separately → combine.',
        yr: 'SS2',
      },
      {
        q: 'Negative vector −A has:',
        o: [
          'Smaller magnitude',
          'Same magnitude, opposite direction',
          'Zero magnitude',
          'No direction',
        ],
        a: 1,
        e: '−A has same magnitude as A but opposite direction.',
        h: 'Negative vector = same magnitude, opposite direction.',
        yr: 'SS2',
      },
      {
        q: 'Unit vector in direction of A is:',
        o: ['A×|A|', 'A/|A|', '|A|/A', 'A²'],
        a: 1,
        e: 'Unit vector = A/|A|. Magnitude = 1, same direction as A.',
        h: 'Unit vector = A/|A| (magnitude 1, same direction).',
        yr: 'SS2',
      },
      {
        q: 'Forces 8 N and 5 N opposite directions. Resultant?',
        o: ['13 N', '3 N in direction of 8 N', '40 N', '3 N in direction of 5 N'],
        a: 1,
        e: 'R = 8−5 = 3 N in direction of larger (8 N) force.',
        h: 'Opposite forces: R = larger−smaller = 3 N in direction of 8 N.',
        yr: 'SS2',
      },
      {
        q: 'Boat east 5 m/s; river south 3 m/s. Resultant speed?',
        o: ['8 m/s', '2 m/s', '√34 m/s south of east', '5.83 m/s south of east'],
        a: 3,
        e: 'R = √(5²+3²) = √34 ≈ 5.83 m/s at angle south of east.',
        h: 'R = √(25+9) = √34 ≈ 5.83 m/s south of east.',
        yr: 'SS2',
      },
      {
        q: 'Which operation on vectors is NOT valid?',
        o: [
          'Adding two vectors',
          'Subtracting one vector from another',
          'Multiplying vector by scalar',
          'Adding scalar to a vector',
        ],
        a: 3,
        e: 'Scalar + vector is undefined/invalid. Vector+vector, vector−vector, scalar×vector are valid.',
        h: 'Invalid: scalar + vector. Valid: vector±vector; scalar×vector.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Motion',
    topicCode: 'SS2-PHY-02',
    module: 'Mechanics',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro">Motion covers equations of uniform acceleration (SUVAT), projectile motion, Newton\'s Laws, and momentum conservation.</div><h3 class="learn-subheading">Key Equations</h3><ul class="learn-list"><li>v = u+at; s = ut+½at²; v²=u²+2as; s=½(u+v)t</li><li>Projectile: horizontal v constant; vertical free fall</li><li>Range R = u²sin2θ/g (max at 45°); H = u²sin²θ/(2g)</li><li>F = ma; p = mv; J = Ft = Δp</li><li>Conservation: total p before = total p after</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> SUVAT equations. Projectile: horizontal constant v + vertical free fall. F=ma. p=mv. Impulse=Ft=Δp. Max range at 45°.</div>',
    questions: [
      {
        q: 'Car from rest, a=2 m/s², velocity after 5 s?',
        o: ['2.5 m/s', '5 m/s', '10 m/s', '25 m/s'],
        a: 2,
        e: 'v = u+at = 0+2×5 = 10 m/s.',
        h: 'v = u+at = 0+10 = 10 m/s.',
        yr: 'SS2',
      },
      {
        q: "Newton's First Law describes:",
        o: [
          'F=ma relationship',
          'Action-reaction pairs',
          'Tendency of objects to resist changes in motion (inertia)',
          'Conservation of momentum',
        ],
        a: 2,
        e: "Newton's 1st Law: a body remains at rest or uniform motion unless acted on by net external force. This is inertia.",
        h: "Newton's 1st Law = inertia: resists change in motion without net force.",
        yr: 'SS2',
      },
      {
        q: '5 kg object, a=3 m/s². Net force?',
        o: ['1.67 N', '8 N', '15 N', '0.6 N'],
        a: 2,
        e: 'F = ma = 5×3 = 15 N.',
        h: 'F = ma = 15 N.',
        yr: 'SS2',
      },
      {
        q: 'Momentum defined as:',
        o: ['Force × time', 'Mass × velocity', 'Mass × acceleration', 'Force × displacement'],
        a: 1,
        e: 'Momentum p = mv. Vector quantity. Unit: kg·m/s.',
        h: 'Momentum p = mv.',
        yr: 'SS2',
      },
      {
        q: 'Horizontal velocity of a projectile (no air resistance):',
        o: ['Zero', 'u (constant)', 'u−gt', 'u+gt'],
        a: 1,
        e: 'No horizontal force → horizontal acceleration = 0 → velocity = u (constant).',
        h: 'Horizontal v = constant = u (no horizontal force).',
        yr: 'SS2',
      },
      {
        q: 'Range of projectile is maximum at angle:',
        o: ['30°', '60°', '45°', '90°'],
        a: 2,
        e: 'R = u²sin2θ/g. Max when sin2θ=1 → θ=45°.',
        h: 'Maximum range at θ = 45°.',
        yr: 'SS2',
      },
      {
        q: "Newton's Third Law:",
        o: [
          'Larger reaction exists',
          'Equal and opposite reaction on different body',
          'No reaction in vacuum',
          'Reaction on same body',
        ],
        a: 1,
        e: 'For every action, equal and opposite reaction acts on a different body.',
        h: "Newton's 3rd: equal and opposite reaction, on different body.",
        yr: 'SS2',
      },
      {
        q: '4 kg at 5 m/s hits stationary 6 kg, stick together. Common velocity?',
        o: ['2 m/s', '5 m/s', '3 m/s', '0.5 m/s'],
        a: 0,
        e: '4×5 = (4+6)v → v = 20/10 = 2 m/s.',
        h: 'm₁u₁=(m₁+m₂)v → 20=10v → v=2 m/s.',
        yr: 'SS2',
      },
      {
        q: 'Impulse defined as:',
        o: [
          'Force × displacement',
          'Force × time = change in momentum',
          'Mass × velocity',
          'Energy × time',
        ],
        a: 1,
        e: 'Impulse J = Ft = Δp (change in momentum). Unit: N·s.',
        h: 'Impulse = Ft = Δp. Unit: N·s.',
        yr: 'SS2',
      },
      {
        q: 'Stone thrown up at 20 m/s, g=10. Time to max height?',
        o: ['1 s', '2 s', '4 s', '10 s'],
        a: 1,
        e: 'v=u−gt; 0=20−10t → t=2 s.',
        h: '0=20−10t → t=2 s.',
        yr: 'SS2',
      },
      {
        q: 'Distance fallen from rest after 3 s (g=10)?',
        o: ['15 m', '30 m', '45 m', '90 m'],
        a: 2,
        e: 's = ½gt² = ½×10×9 = 45 m.',
        h: 's = ½gt² = 45 m.',
        yr: 'SS2',
      },
      {
        q: 'In elastic collision:',
        o: [
          'Momentum conserved, KE lost',
          'Both momentum AND kinetic energy conserved',
          'Only KE conserved',
          'Objects stick together',
        ],
        a: 1,
        e: 'Elastic: both momentum and kinetic energy are conserved.',
        h: 'Elastic collision: momentum AND KE both conserved.',
        yr: 'SS2',
      },
      {
        q: 'V-t graph for uniform acceleration is:',
        o: ['Horizontal line', 'Parabola', 'Straight line with positive gradient', 'Vertical line'],
        a: 2,
        e: 'v = u+at is linear → straight line on v-t graph. Gradient = acceleration; area = displacement.',
        h: 'Uniform acceleration: straight-line v-t graph. Gradient = a; area = s.',
        yr: 'SS2',
      },
      {
        q: 'Conservation of momentum states:',
        o: [
          'Momentum increases with velocity',
          'Total momentum constant if no external force acts',
          'Momentum always zero after collision',
          'Momentum equals KE',
        ],
        a: 1,
        e: 'In a closed system (no external forces), total momentum before = total momentum after.',
        h: 'Total p constant: no external forces → conservation of momentum.',
        yr: 'SS2',
      },
      {
        q: 'Vehicle at 30 m/s stops in 6 s. Deceleration?',
        o: ['5 m/s²', '180 m/s²', '0.2 m/s²', '36 m/s²'],
        a: 0,
        e: 'a = (v−u)/t = (0−30)/6 = −5 m/s². Magnitude = 5 m/s².',
        h: 'a = (0−30)/6 = −5 m/s².',
        yr: 'SS2',
      },
      {
        q: 'Max height of projectile at 30 m/s, 60°, g=10?',
        o: ['22.5 m', '33.75 m', '45 m', '67.5 m'],
        a: 1,
        e: 'H = u²sin²θ/(2g) = 900×0.75/20 = 33.75 m.',
        h: 'H = 900×(3/4)/20 = 33.75 m.',
        yr: 'SS2',
      },
      {
        q: 'Impulse has the same unit as:',
        o: ['Force', 'Energy', 'Momentum', 'Power'],
        a: 2,
        e: 'Impulse = N·s = kg·m/s = unit of momentum.',
        h: 'Impulse (N·s) = Momentum (kg·m/s) — same unit.',
        yr: 'SS2',
      },
      {
        q: 'Constant velocity means:',
        o: ['Zero momentum', 'Net force = zero', 'Acceleration = velocity', 'KE = zero'],
        a: 1,
        e: 'Constant velocity → zero acceleration → by F=ma, net force = zero.',
        h: 'Constant velocity → a=0 → net force = 0.',
        yr: 'SS2',
      },
      {
        q: 'Area under v-t graph represents:',
        o: ['Acceleration', 'Displacement', 'Force', 'Power'],
        a: 1,
        e: 'Area under v-t graph = displacement. (s = average v × t)',
        h: 'Area under v-t graph = displacement.',
        yr: 'SS2',
      },
      {
        q: '2 kg at 8 m/s E hits stationary 2 kg. First object moves at 2 m/s E after. Second object velocity?',
        o: ['6 m/s east', '10 m/s east', '4 m/s west', '6 m/s west'],
        a: 0,
        e: '2×8+0=2×2+2×v₂ → 16=4+2v₂ → v₂=6 m/s east.',
        h: 'Conservation: 16=4+2v₂ → v₂=6 m/s east.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Gravitational Field',
    topicCode: 'SS2-PHY-03',
    module: 'Mechanics',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro">Newton\'s Law of Universal Gravitation: F=Gm₁m₂/r². Gravitational field strength g=GM/r². Weight W=mg. Satellites: gravity = centripetal force. Escape velocity = √(2GM/R).</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> F=Gm₁m₂/r² (inverse square). g=GM/r² ≈ 9.8 m/s² at surface. Weight W=mg (vector). Satellite: v=√(GM/r). Kepler: T²∝r³. Escape velocity ≈ 11.2 km/s.</div>',
    questions: [
      {
        q: "Newton's Law of Universal Gravitation: F is proportional to:",
        o: [
          'Sum of masses / distance',
          'Product of masses / distance²',
          'Product of masses / distance',
          'Sum of masses × distance',
        ],
        a: 1,
        e: 'F = Gm₁m₂/r². Proportional to product of masses, inversely proportional to r².',
        h: 'F = Gm₁m₂/r². Proportional to m₁m₂; inversely proportional to r².',
        yr: 'SS2',
      },
      {
        q: 'Value of universal gravitational constant G:',
        o: ['9.8 m/s²', '6.67 × 10⁻¹¹ N·m²/kg²', '3 × 10⁸ m/s', '1.6 × 10⁻¹⁹ C'],
        a: 1,
        e: 'G = 6.67 × 10⁻¹¹ N·m²/kg². (Not the same as g = 9.8 m/s².)',
        h: 'G = 6.67 × 10⁻¹¹ N·m²/kg².',
        yr: 'SS2',
      },
      {
        q: 'Distance between masses doubled. Force becomes:',
        o: ['Double', 'Half', 'One-quarter', 'One-eighth'],
        a: 2,
        e: 'F ∝ 1/r². r doubled → F = F/4 (one-quarter).',
        h: 'r doubled → F reduced to one-quarter (inverse square law).',
        yr: 'SS2',
      },
      {
        q: 'Weight differs from mass in that weight:',
        o: [
          'Measured in kg',
          'Same everywhere',
          'Is gravitational force W=mg (Newtons), varies with location',
          'Has no direction',
        ],
        a: 2,
        e: 'Weight W=mg is a force (N), varies with g. Mass is constant (kg).',
        h: 'Weight W=mg (force, N, varies). Mass = constant (kg).',
        yr: 'SS2',
      },
      {
        q: "Gravitational field strength g at Earth's surface equals:",
        o: [
          'G (universal constant)',
          'GM_Earth/R²_Earth ≈ 9.8 m/s²',
          'GM_Earth×R²_Earth',
          'M_Earth/G',
        ],
        a: 1,
        e: "g = GM/R² ≈ 9.8 m/s² at Earth's surface.",
        h: 'g = GM/R² ≈ 9.8 m/s².',
        yr: 'SS2',
      },
      {
        q: 'g varies with altitude by:',
        o: [
          'Increasing',
          'Staying constant',
          'Decreasing (g=GM/r², r increases)',
          'First increasing then decreasing',
        ],
        a: 2,
        e: 'g = GM/r². As altitude increases, r increases, g decreases.',
        h: 'g decreases with altitude: g = GM/r².',
        yr: 'SS2',
      },
      {
        q: 'For a satellite in circular orbit, gravity provides:',
        o: [
          'Driving force to speed up',
          'Centripetal force',
          'Energy for electronics',
          'Escape force',
        ],
        a: 1,
        e: 'GMm/r² = mv²/r. Gravity = centripetal force for circular orbit.',
        h: 'Satellite: gravity = centripetal force. GMm/r² = mv²/r.',
        yr: 'SS2',
      },
      {
        q: 'Orbital speed at distance r from Earth:',
        o: ['√(gr)', '√(GM/r)', '√(2GM/r)', 'GM/r²'],
        a: 1,
        e: 'From GMm/r² = mv²/r: v = √(GM/r).',
        h: 'Orbital speed v = √(GM/r).',
        yr: 'SS2',
      },
      {
        q: "Kepler's Third Law: T² ∝",
        o: ['r', 'r²', 'r³', 'r⁴'],
        a: 2,
        e: "Kepler's 3rd: T² ∝ r³. Farther orbit → longer period.",
        h: "Kepler's 3rd Law: T² ∝ r³.",
        yr: 'SS2',
      },
      {
        q: 'Earth escape velocity ≈',
        o: ['7.9 km/s', '11.2 km/s', '3 × 10⁸ m/s', '9.8 km/s'],
        a: 1,
        e: 'vₑ = √(2GM/R) ≈ 11.2 km/s for Earth.',
        h: 'Escape velocity ≈ 11.2 km/s = √(2GM/R).',
        yr: 'SS2',
      },
      {
        q: 'Geostationary satellite:',
        o: [
          'Orbits every 12 hours',
          'Orbits above equator, period 24 hours, appears stationary',
          'Orbits at very low altitude',
          'Is in polar orbit',
        ],
        a: 1,
        e: 'Geostationary: period = 24 h, equatorial orbit, appears stationary from Earth. Used for communications.',
        h: 'Geostationary: 24 h period, equatorial, stationary from Earth.',
        yr: 'SS2',
      },
      {
        q: 'Astronaut in circular orbit experiences:',
        o: [
          'Weight greater than on Earth',
          'Weightlessness (continuous free fall)',
          'Half their Earth weight',
          'No change in weight',
        ],
        a: 1,
        e: 'In orbit, continuous free fall → apparent weightlessness. Gravity still acts but produces no normal force.',
        h: 'Orbital astronaut: weightless (continuous free fall).',
        yr: 'SS2',
      },
      {
        q: 'One mass doubled, distance tripled. New force vs original?',
        o: ['2/9 of original', '2/3', '4/9', '6/9'],
        a: 0,
        e: "F' = G(2M)(M)/(3d)² = 2GM²/9d² = (2/9)F.",
        h: "F' = G(2M)M/(3d)² = (2/9)F.",
        yr: 'SS2',
      },
      {
        q: 'Gravitational field is a vector field because:',
        o: [
          'It has magnitude only',
          'Gravitational force has magnitude AND direction (toward attracting mass)',
          'Gravity has no direction',
          'g has no unit',
        ],
        a: 1,
        e: 'g is a vector: magnitude = GM/r²; direction = toward the attracting mass.',
        h: 'Gravitational field = vector (magnitude GM/r²; direction toward mass).',
        yr: 'SS2',
      },
      {
        q: '60 kg on Earth weighs 600 N. On planet where g=4 m/s²:',
        o: ['600 N', '240 N', '150 N', '60 N'],
        a: 1,
        e: 'W = mg = 60×4 = 240 N. Mass unchanged; weight varies with g.',
        h: 'W = 60×4 = 240 N.',
        yr: 'SS2',
      },
      {
        q: "GPE near Earth's surface:",
        o: ['mgh', 'mv²/2', 'mg/h', 'mgh²'],
        a: 0,
        e: 'GPE = mgh (near surface where g is approximately constant).',
        h: 'GPE = mgh.',
        yr: 'SS2',
      },
      {
        q: "Minimum orbital speed just above Earth's surface (g=10, R=6.4×10⁶ m):",
        o: ['~7.9 km/s = √(gR)', '11.2 km/s', '9.8 km/s', '3×10⁸ m/s'],
        a: 0,
        e: 'v = √(gR) = √(10×6.4×10⁶) ≈ 8000 m/s ≈ 7.9 km/s.',
        h: 'Minimum orbital speed = √(gR) ≈ 7.9 km/s.',
        yr: 'SS2',
      },
      {
        q: 'Both masses tripled, force changes by:',
        o: ['×3', '×6', '×9', '×27'],
        a: 2,
        e: "F' = G(3m)(3m)/r² = 9F. Force × 9.",
        h: "Both masses ×3: F' = 9F.",
        yr: 'SS2',
      },
      {
        q: 'Gravitational field inside uniform hollow sphere:',
        o: [
          'Maximum at centre',
          'Same as surface',
          'Zero everywhere inside',
          '∝ distance from centre',
        ],
        a: 2,
        e: 'Shell theorem: g = 0 inside a uniform hollow sphere. All contributions cancel.',
        h: 'Inside hollow sphere: g = 0 (shell theorem).',
        yr: 'SS2',
      },
      {
        q: 'Tides are primarily caused by:',
        o: [
          "Earth's rotation only",
          'Differential gravitational force of the Moon across Earth',
          "Sun's radiation pressure",
          "Earth's elliptical orbit",
        ],
        a: 1,
        e: 'Tides = differential (tidal) gravitational force of the Moon — stronger on near side, weaker on far side.',
        h: "Tides = differential Moon gravity across Earth's diameter.",
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Equilibrium of Forces',
    topicCode: 'SS2-PHY-04',
    module: 'Mechanics',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro">Equilibrium: ΣF=0 (translational) AND Στ=0 (rotational). Moment = F × perpendicular distance. Principle of moments: clockwise = anticlockwise.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> ΣF=0 (1st condition). Στ=0 (2nd condition). Moment=F×d⊥. CW moments = ACW moments. Stable: low CG, wide base. Unstable: high CG. Couple = two equal opposite non-collinear forces.</div>',
    questions: [
      {
        q: 'First condition for equilibrium:',
        o: [
          'Sum of moments = zero',
          'Vector sum of all forces = zero',
          'Body must be at rest',
          'CG at geometric centre',
        ],
        a: 1,
        e: '1st condition: ΣF=0 in all directions (translational equilibrium).',
        h: '1st condition: ΣF = 0 (no net force).',
        yr: 'SS2',
      },
      {
        q: 'Moment of a force = ',
        o: [
          'Force × velocity',
          'Force × perpendicular distance from pivot',
          'Force × mass',
          'Force / distance',
        ],
        a: 1,
        e: 'Moment (torque) τ = F × d⊥. Unit: N·m.',
        h: 'Moment = F × perpendicular distance. Unit: N·m.',
        yr: 'SS2',
      },
      {
        q: '30 N at 1 m left of pivot; balance by F at 1.5 m right. F = ?',
        o: ['30 N', '20 N', '45 N', '15 N'],
        a: 1,
        e: '30×1 = F×1.5 → F = 20 N.',
        h: '30×1 = F×1.5 → F = 20 N.',
        yr: 'SS2',
      },
      {
        q: 'Stable equilibrium: body returns to original position. Characterised by:',
        o: [
          'High CG, narrow base',
          'Low CG and wide base',
          'CG at geometric centre',
          'Equal forces from all sides',
        ],
        a: 1,
        e: 'Stable: low CG + wide base → returns when displaced.',
        h: 'Stable = low CG + wide base.',
        yr: 'SS2',
      },
      {
        q: 'A couple consists of:',
        o: [
          'Two equal forces same direction',
          'Two equal opposite non-collinear forces → pure rotation',
          'Single force with two components',
          'Two unequal forces at 90°',
        ],
        a: 1,
        e: 'Couple: two equal, opposite forces not through same line → torque = Fd, no net force, pure rotation.',
        h: 'Couple = two equal opposite non-collinear forces → pure rotation.',
        yr: 'SS2',
      },
      {
        q: 'Principle of moments for equilibrium:',
        o: [
          'All forces equal',
          'Clockwise moments = anticlockwise moments',
          'Total force = total moment',
          'Moments at 90° to force',
        ],
        a: 1,
        e: 'Principle of moments: ΣCW moments = ΣACW moments (Στ=0).',
        h: 'Principle of moments: CW = ACW (2nd condition for equilibrium).',
        yr: 'SS2',
      },
      {
        q: 'Unstable equilibrium: displaced body:',
        o: [
          'Returns to original position',
          'Moves further from original position; high CG',
          'Stays in new position',
          'Has zero PE',
        ],
        a: 1,
        e: 'Unstable: high CG → small displacement → body topples further away.',
        h: 'Unstable = high CG → topples further when displaced.',
        yr: 'SS2',
      },
      {
        q: 'Force 15 N at 0.4 m from pivot. Moment?',
        o: ['37.5 N·m', '6 N·m', '0.4 N·m', '15.4 N·m'],
        a: 1,
        e: 'Moment = 15×0.4 = 6 N·m.',
        h: 'Moment = 15×0.4 = 6 N·m.',
        yr: 'SS2',
      },
      {
        q: 'CG of uniform rectangular lamina is at:',
        o: [
          'Any corner',
          'Centre (intersection of diagonals)',
          'Midpoint of one edge',
          '1/3 from one edge',
        ],
        a: 1,
        e: 'CG of uniform rectangle = geometric centre (intersection of diagonals).',
        h: 'CG uniform rectangle = centre.',
        yr: 'SS2',
      },
      {
        q: 'Body topples when:',
        o: [
          'CG at geometric centre',
          'Vertical line through CG falls OUTSIDE base of support',
          'Base is very wide',
          'All forces equal',
        ],
        a: 1,
        e: 'Toppling occurs when vertical line through CG falls outside the base of support.',
        h: 'Topples when CG vertical line falls outside base of support.',
        yr: 'SS2',
      },
      {
        q: 'Neutral equilibrium: displaced body:',
        o: [
          'Returns to original',
          'Topples further',
          'Stays in new position (CG unchanged height)',
          'Has zero weight',
        ],
        a: 2,
        e: 'Neutral: CG stays at same height when displaced → body stays in new position. Example: sphere on flat surface.',
        h: 'Neutral: CG same height when displaced → stays in new position.',
        yr: 'SS2',
      },
      {
        q: "Uniform metre rule's weight acts at:",
        o: ['40 cm', '50 cm (centre)', '60 cm', '90 cm'],
        a: 1,
        e: 'Uniform metre rule: CG at 50 cm (geometric centre).',
        h: 'Uniform metre rule weight acts at 50 cm mark.',
        yr: 'SS2',
      },
      {
        q: 'Second condition for equilibrium ensures:',
        o: [
          'Translational equilibrium',
          'Rotational equilibrium: Στ=0 about any point',
          'All x-direction forces balanced',
          'KE conserved',
        ],
        a: 1,
        e: '2nd condition: Στ=0 → no rotational acceleration. Together with ΣF=0 → full equilibrium.',
        h: '2nd condition: Στ = 0 (no rotation).',
        yr: 'SS2',
      },
      {
        q: 'Most stable vehicle has:',
        o: ['High CG', 'Narrow wheelbase', 'Low CG and wide wheelbase', 'Heavy engine at top'],
        a: 2,
        e: 'Maximum stability: low CG + wide wheelbase (base). Reduces toppling tendency.',
        h: 'Most stable: low CG + wide base/wheelbase.',
        yr: 'SS2',
      },
      {
        q: 'Ladder on smooth wall: forces acting on it are:',
        o: [
          'Only gravity and ground friction',
          'Wall normal (horizontal), ground normal (vertical), ground friction (horizontal), weight (vertical)',
          'Only weight and ground normal',
          'Only tension/compression',
        ],
        a: 1,
        e: 'Smooth wall: no friction from wall, only normal (horizontal). Ground: normal + friction. Plus weight.',
        h: 'Ladder forces: wall normal + ground normal + ground friction + weight.',
        yr: 'SS2',
      },
      {
        q: 'Couple torque with F=5 N, d=0.3 m:',
        o: ['5 N·m', '1.5 N·m', '0.3 N·m', '10 N·m'],
        a: 1,
        e: 'Couple torque = F×d = 5×0.3 = 1.5 N·m.',
        h: 'Couple torque = F×d = 1.5 N·m.',
        yr: 'SS2',
      },
      {
        q: 'Experimental CG of irregular lamina found by:',
        o: [
          'Weighing on balance',
          'Hanging from two points, drawing plumb lines; CG = intersection',
          'Measuring geometric centre',
          'Submerging in water',
        ],
        a: 1,
        e: 'Hang from two points; plumb line below each. CG = intersection of the two vertical lines.',
        h: 'CG of irregular lamina: hang from 2 points; plumb lines intersect at CG.',
        yr: 'SS2',
      },
      {
        q: 'Uniform plank, weight 100 N, length 4 m, on two end supports; 200 N load 1 m from left. Reaction at left support?',
        o: ['250 N', '175 N', '125 N', '200 N'],
        a: 3,
        e: 'Moments about right support: R_L×4 = 200×3+100×2 = 800. R_L = 200 N. Check: R_R = 300−200 = 100 N.',
        h: 'R_L×4 = 200×3+100×2 = 800 → R_L = 200 N.',
        yr: 'SS2',
      },
      {
        q: 'Ground friction essential for ladder on smooth wall because:',
        o: [
          'Supports full weight',
          "Prevents base sliding outward (horizontal reaction to wall's normal force)",
          'Provides moment to lift ladder',
          'Counteracts wall friction',
        ],
        a: 1,
        e: 'Smooth wall: only horizontal normal force pushing base outward. Ground friction resists this horizontally.',
        h: 'Ground friction prevents ladder base sliding outward.',
        yr: 'SS2',
      },
      {
        q: 'Sum of all moments about ANY point = 0 is the:',
        o: [
          'First condition for equilibrium',
          'Second condition for equilibrium (Στ=0)',
          'Definition of a couple',
          "Statement of Newton's 3rd law",
        ],
        a: 1,
        e: 'Second condition: Στ=0 about any point → rotational equilibrium.',
        h: 'Στ=0 about any point = 2nd condition for equilibrium.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Simple Harmonic Motion',
    topicCode: 'SS2-PHY-05',
    module: 'Mechanics',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro">SHM: a = −ω²x (proportional to displacement, toward equilibrium). Pendulum: T=2π√(L/g). Spring: T=2π√(m/k). At equilibrium: max KE. At amplitude: max PE.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> a=−ω²x. T=2π/ω. Pendulum T=2π√(L/g) — depends only on L and g. Spring T=2π√(m/k). v_max=ωA. a_max=ω²A. Total E=½kA²=constant.</div>',
    questions: [
      {
        q: 'In SHM, acceleration is:',
        o: [
          'Constant',
          'Proportional to displacement, directed away from equilibrium',
          'Proportional to displacement, directed toward equilibrium (restoring)',
          'Zero always',
        ],
        a: 2,
        e: 'SHM: a=−ω²x. Proportional to displacement; directed toward equilibrium (restoring force).',
        h: 'SHM: a=−ω²x (restoring, proportional to displacement).',
        yr: 'SS2',
      },
      {
        q: 'Period of pendulum L=1 m, g=10 m/s²?',
        o: ['0.5 s', '1 s', '2 s', '3.14 s'],
        a: 2,
        e: 'T = 2π√(1/10) = 2π/√10 ≈ 2 s.',
        h: 'T = 2π√(L/g) = 2π√(0.1) ≈ 2 s.',
        yr: 'SS2',
      },
      {
        q: 'Which does NOT affect pendulum period?',
        o: ['Length', 'Value of g', 'Mass of bob', 'Both A and B affect it'],
        a: 2,
        e: 'T=2π√(L/g). Period depends on L and g ONLY. Mass has NO effect.',
        h: 'Pendulum period: only L and g matter. Mass has NO effect.',
        yr: 'SS2',
      },
      {
        q: 'At equilibrium position of SHM:',
        o: [
          'Max PE, zero KE',
          'Zero velocity, max acceleration',
          'Max velocity, zero PE (max KE)',
          'Zero displacement and zero velocity',
        ],
        a: 2,
        e: 'At equilibrium: x=0; v=max; a=0; KE=max; PE=0.',
        h: 'At equilibrium: v=max; KE=max; PE=0.',
        yr: 'SS2',
      },
      {
        q: 'At amplitude (extreme point) of SHM:',
        o: [
          'Maximum velocity',
          'Max KE, zero acceleration',
          'Zero velocity, max acceleration, max PE',
          'Zero displacement',
        ],
        a: 2,
        e: 'At amplitude: x=max; v=0; a=max; KE=0; PE=max.',
        h: 'At amplitude: v=0; a=max; KE=0; PE=max.',
        yr: 'SS2',
      },
      {
        q: 'Spring k=100 N/m, m=0.25 kg. Period?',
        o: ['0.314 s', '0.628 s', '1 s', '3.14 s'],
        a: 0,
        e: 'T=2π√(m/k)=2π√(0.25/100)=2π×0.05≈0.314 s.',
        h: 'T=2π√(0.25/100)=2π×0.05≈0.314 s.',
        yr: 'SS2',
      },
      {
        q: 'Maximum velocity in SHM:',
        o: ['ωA²', 'ωA (at equilibrium)', 'ω²A', 'A/ω'],
        a: 1,
        e: 'v_max = ωA, at equilibrium position (x=0).',
        h: 'v_max = ωA (at equilibrium).',
        yr: 'SS2',
      },
      {
        q: 'Total mechanical energy in SHM (no damping):',
        o: ['Increases with time', 'Decreases with time', 'Constant = ½kA²', 'Always zero'],
        a: 2,
        e: 'Total E = ½kA² = constant (no damping). KE↔PE exchange, total unchanged.',
        h: 'Total E in SHM = ½kA² = constant.',
        yr: 'SS2',
      },
      {
        q: 'Pendulum length quadrupled. Period:',
        o: ['Doubles', 'Quadruples', 'Halves', 'Same'],
        a: 0,
        e: 'T∝√L. L×4 → T×√4=2T. Period doubles.',
        h: 'T∝√L. L×4 → T×2 (doubles).',
        yr: 'SS2',
      },
      {
        q: 'ω related to T by:',
        o: ['ω=T', 'ω=2π/T', 'ω=T/2π', 'ω=1/T'],
        a: 1,
        e: 'ω=2π/T=2πf.',
        h: 'ω = 2π/T.',
        yr: 'SS2',
      },
      {
        q: 'SHM example:',
        o: [
          'Car at constant speed',
          'Satellite in circular orbit',
          'Mass on spring oscillating',
          'Ball rolling down incline',
        ],
        a: 2,
        e: 'Mass-spring: a=−(k/m)x → SHM definition satisfied.',
        h: 'Mass on spring: a=−(k/m)x → SHM.',
        yr: 'SS2',
      },
      {
        q: 'Damped oscillations: amplitude:',
        o: [
          'Increases with time',
          'Decreases due to energy loss (friction/air resistance)',
          'Stays constant',
          'Doubles each cycle',
        ],
        a: 1,
        e: 'Damped oscillations: energy lost → amplitude decreases over time. Period approximately unchanged (light damping).',
        h: 'Damped: amplitude decreases due to energy loss.',
        yr: 'SS2',
      },
      {
        q: 'Resonance occurs when:',
        o: [
          'Driving frequency >> natural frequency',
          'Driving frequency = natural frequency → maximum amplitude',
          'Damping is very high',
          'System is at rest',
        ],
        a: 1,
        e: 'Resonance: driving frequency = natural frequency → maximum energy absorption → maximum amplitude.',
        h: 'Resonance: driving f = natural f → maximum amplitude.',
        yr: 'SS2',
      },
      {
        q: 'To double period of mass-spring system:',
        o: [
          'Double spring constant',
          'Halve spring constant',
          'Quadruple mass (T∝√m)',
          'Double amplitude',
        ],
        a: 2,
        e: "T∝√m. To double T: m' = 4m (quadruple mass).",
        h: 'T∝√m. Double T → mass must be ×4.',
        yr: 'SS2',
      },
      {
        q: 'Pendulum taken to mountain top will:',
        o: ['Keep same time', 'Run fast', 'Run slow (lower g → longer period)', 'Stop oscillating'],
        a: 2,
        e: 'T=2π√(L/g). Higher altitude → smaller g → larger T → runs slow.',
        h: 'Mountain: smaller g → longer T → pendulum runs slow.',
        yr: 'SS2',
      },
      {
        q: 'Velocity at displacement x in SHM:',
        o: ['v=ωx', 'v=ω√(A²−x²)', 'v=ω²(A−x)', 'v=A/ω'],
        a: 1,
        e: 'v=ω√(A²−x²). At x=0: v=ωA (max). At x=A: v=0.',
        h: 'v=ω√(A²−x²).',
        yr: 'SS2',
      },
      {
        q: 'Spring stretches 0.02 m under 10 N. k=?',
        o: ['0.2 N/m', '200 N/m', '500 N/m', '50 N/m'],
        a: 2,
        e: 'k=F/x=10/0.02=500 N/m.',
        h: 'k=F/x=10/0.02=500 N/m.',
        yr: 'SS2',
      },
      {
        q: 'Pendulum length from 1 m to 4 m. Period change?',
        o: ['Doubles', 'Quadruples', 'Same', 'Halves'],
        a: 0,
        e: 'T∝√L. L×4 → T×2. Period doubles.',
        h: 'T∝√L. L×4 → T doubles.',
        yr: 'SS2',
      },
      {
        q: 'During one complete SHM oscillation, restoring force changes direction:',
        o: ['Never', 'Once', 'Twice (at each amplitude extreme)', 'Continuously'],
        a: 2,
        e: 'Restoring force reverses at each extreme (+A and −A) — twice per cycle.',
        h: 'Restoring force reverses direction twice per complete oscillation.',
        yr: 'SS2',
      },
      {
        q: 'SHM condition requires that acceleration is:',
        o: [
          'Constant',
          'Proportional to displacement squared',
          'Proportional to displacement and oppositely directed (a=−ω²x)',
          'Equal to velocity',
        ],
        a: 2,
        e: 'SHM condition: a=−ω²x. Proportional to displacement, opposite direction (restoring).',
        h: 'SHM condition: a=−ω²x (proportional, opposite to displacement).',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Mechanical Energy',
    topicCode: 'SS2-PHY-06',
    module: 'Energy',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro">W=Fs cosθ. KE=½mv². GPE=mgh. EPE=½kx². Conservation: KE+PE=constant (no friction). Power P=W/t=Fv. Efficiency=MA/VR×100%.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> W=Fs cosθ. KE=½mv². GPE=mgh. Conservation of ME. P=W/t=Fv. Efficiency=(MA/VR)×100%.</div>',
    questions: [
      {
        q: 'KE of 2 kg at 6 m/s?',
        o: ['12 J', '36 J', '6 J', '72 J'],
        a: 1,
        e: 'KE=½mv²=½×2×36=36 J.',
        h: 'KE=½×2×36=36 J.',
        yr: 'SS2',
      },
      {
        q: 'Work done = 0 when:',
        o: [
          'Force is very large',
          'Displacement in direction of force',
          'Force perpendicular to displacement (cos90°=0)',
          'Mass is very small',
        ],
        a: 2,
        e: 'W=Fs cosθ. θ=90° → cos90°=0 → W=0.',
        h: 'W=Fs cosθ. θ=90° → W=0.',
        yr: 'SS2',
      },
      {
        q: '5 kg raised 4 m (g=10). GPE gained?',
        o: ['20 J', '50 J', '200 J', '2000 J'],
        a: 2,
        e: 'GPE=mgh=5×10×4=200 J.',
        h: 'GPE=mgh=200 J.',
        yr: 'SS2',
      },
      {
        q: 'Work-energy theorem states:',
        o: ['Work=PE', 'Net work=ΔKE=½mv²−½mu²', 'Work=Power×Force', 'Net work=mv'],
        a: 1,
        e: 'Work-energy theorem: W_net=ΔKE=½mv²−½mu².',
        h: 'W_net=ΔKE.',
        yr: 'SS2',
      },
      {
        q: '0.5 kg dropped from 20 m, speed just before hitting ground (g=10)?',
        o: ['10 m/s', '14.1 m/s', '20 m/s', '200 m/s'],
        a: 2,
        e: 'mgh=½mv² → v=√(2gh)=√400=20 m/s.',
        h: 'v=√(2gh)=√400=20 m/s.',
        yr: 'SS2',
      },
      {
        q: 'Power defined as:',
        o: ['Force × velocity²', 'P=W/t=Fv', 'Work × time', 'Force / time'],
        a: 1,
        e: 'P=W/t=Fv. Unit: Watt (W=J/s).',
        h: 'Power P=W/t=Fv.',
        yr: 'SS2',
      },
      {
        q: '1200 W motor lifts 60 kg load (g=10). Max speed?',
        o: ['0.5 m/s', '2 m/s', '5 m/s', '20 m/s'],
        a: 1,
        e: 'P=mgv → 1200=600v → v=2 m/s.',
        h: '1200=600v → v=2 m/s.',
        yr: 'SS2',
      },
      {
        q: 'EPE in spring compressed 0.1 m (k=500 N/m)?',
        o: ['2.5 J', '25 J', '50 J', '250 J'],
        a: 0,
        e: 'EPE=½kx²=½×500×0.01=2.5 J.',
        h: 'EPE=½×500×0.01=2.5 J.',
        yr: 'SS2',
      },
      {
        q: 'Ball thrown upward; at highest point:',
        o: ['All KE', 'All PE; KE=0', 'KE=PE', 'Total E=zero'],
        a: 1,
        e: 'Highest point: v=0 → KE=0; all energy is GPE=mgh.',
        h: 'Highest point: KE=0; all energy is GPE.',
        yr: 'SS2',
      },
      {
        q: 'Machine efficiency:',
        o: ['Load × effort', '(Useful output / total input) × 100%', 'VR / MA', 'Power × time'],
        a: 1,
        e: 'Efficiency=(useful output/total input)×100%=(MA/VR)×100%.',
        h: 'Efficiency=(useful output/total input)×100%.',
        yr: 'SS2',
      },
      {
        q: 'Car 1000 kg accelerates 0 to 20 m/s. Work by engine?',
        o: ['10,000 J', '100,000 J', '200,000 J', '400,000 J'],
        a: 2,
        e: 'W=ΔKE=½×1000×400=200,000 J.',
        h: 'W=½mv²=½×1000×400=200,000 J.',
        yr: 'SS2',
      },
      {
        q: 'MA = ',
        o: [
          'Effort distance / load distance',
          'Load / effort',
          'Power out / power in',
          'Velocity of load / velocity of effort',
        ],
        a: 1,
        e: 'MA=Load/Effort. MA>1: machine multiplies force.',
        h: 'MA=Load/Effort.',
        yr: 'SS2',
      },
      {
        q: 'Conservation of ME applies when:',
        o: [
          'Always friction present',
          'Only gravity acts',
          'No non-conservative forces; KE+PE=constant',
          'Object stationary',
        ],
        a: 2,
        e: 'ME conserved when no non-conservative forces (friction/drag): KE+PE=constant.',
        h: 'ME conserved: KE+PE=constant (no friction).',
        yr: 'SS2',
      },
      {
        q: '50 N moves object 10 m at 60° to force. Work done?',
        o: ['500 J', '433 J', '250 J', '866 J'],
        a: 2,
        e: 'W=Fs cosθ=50×10×cos60°=500×0.5=250 J.',
        h: 'W=50×10×cos60°=250 J.',
        yr: 'SS2',
      },
      {
        q: 'Single fixed pulley: MA=1, VR=1. Efficiency?',
        o: ['50%', '75%', '100%', '0%'],
        a: 2,
        e: 'η=(MA/VR)×100%=(1/1)×100%=100% (ideal).',
        h: 'Single fixed pulley: MA=VR=1; efficiency=100%.',
        yr: 'SS2',
      },
      {
        q: '70 kg man runs up stairs 5 m in 7 s (g=10). Power?',
        o: ['70 W', '350 W', '500 W', '3500 W'],
        a: 2,
        e: 'P=mgh/t=70×10×5/7=500 W.',
        h: 'P=mgh/t=3500/7=500 W.',
        yr: 'SS2',
      },
      {
        q: 'Block and tackle with 4 rope segments supporting load: VR=',
        o: ['1', '2', '4', '8'],
        a: 2,
        e: 'VR = number of rope segments supporting the movable block = 4.',
        h: 'Block and tackle VR = number of supporting rope segments.',
        yr: 'SS2',
      },
      {
        q: 'Machine does 6000 J in 2 minutes. Power?',
        o: ['3000 W', '50 W', '6000 W', '100 W'],
        a: 1,
        e: 'P=W/t=6000/120=50 W.',
        h: 'P=6000/120=50 W.',
        yr: 'SS2',
      },
      {
        q: 'Spring k=800 N/m stretched 0.05 m. Restoring force?',
        o: ['16000 N', '40 N', '0.000063 N', '4 N'],
        a: 1,
        e: 'F=kx=800×0.05=40 N.',
        h: 'F=kx=800×0.05=40 N.',
        yr: 'SS2',
      },
      {
        q: '2 kg at rest, F=8 N for 3 s. Work done by net force?',
        o: ['48 J', '144 J', '72 J', '24 J'],
        a: 1,
        e: 'a=4 m/s²; v=12 m/s; W=ΔKE=½×2×144=144 J.',
        h: 'W=ΔKE=½×2×144=144 J.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Properties of Matter',
    topicCode: 'SS2-PHY-07',
    module: 'Matter',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro">Elasticity: Hooke\'s Law F=ke (within elastic limit). Young\'s Modulus E=stress/strain=FL/(Ae). Surface tension: force/length. Capillarity: adhesion vs cohesion. Viscosity η: Stokes F=6πηrv. Fluid pressure P=ρgh.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Hooke: F=ke. E=FL/(Ae). Stress=F/A (Pa). Strain=e/L (dimensionless). Capillarity: water rises (adhesion>cohesion). P=ρgh. Pascal: F₁/A₁=F₂/A₂. Archimedes: upthrust=weight of fluid displaced.</div>',
    questions: [
      {
        q: "Hooke's Law (elastic limit): extension is:",
        o: [
          'Inversely proportional to force',
          'Directly proportional to force: F=ke',
          'Independent of force',
          'Proportional to force squared',
        ],
        a: 1,
        e: "Hooke's Law: F=ke (force = spring constant × extension), valid within elastic limit.",
        h: "Hooke's Law: F=ke.",
        yr: 'SS2',
      },
      {
        q: "Young's Modulus E =",
        o: ['Stress×strain', 'Stress/strain = (F/A)/(e/L)', 'Strain/stress', 'Force/extension'],
        a: 1,
        e: 'E=Stress/Strain=(F/A)/(e/L)=FL/(Ae). Unit: Pa.',
        h: 'E=Stress/Strain=FL/(Ae).',
        yr: 'SS2',
      },
      {
        q: 'Stress has same unit as:',
        o: ['Strain', 'Pressure (Pa=N/m²)', 'Energy (J)', 'Velocity (m/s)'],
        a: 1,
        e: 'Stress=F/A (N/m²=Pa). Same dimensions as pressure.',
        h: 'Stress=F/A; same unit as pressure: Pa.',
        yr: 'SS2',
      },
      {
        q: 'Surface tension caused by:',
        o: [
          'Gravity pulling molecules',
          'Cohesive intermolecular forces pulling surface molecules inward',
          'Atmospheric pressure',
          'Weight of liquid above',
        ],
        a: 1,
        e: 'Surface tension: cohesive forces on surface molecules from below/sides → net inward pull → surface tension.',
        h: 'Surface tension: cohesive forces pull surface molecules inward.',
        yr: 'SS2',
      },
      {
        q: 'Water rises in narrow glass tube due to:',
        o: [
          'Viscosity',
          'Adhesive forces (water-glass) > cohesive forces (water-water) — capillarity',
          'Atmospheric pressure from above',
          'Surface tension only',
        ],
        a: 1,
        e: 'Capillary rise: adhesion (water-glass) > cohesion (water-water). Concave meniscus.',
        h: 'Capillary rise: adhesion > cohesion. Water rises in glass tube.',
        yr: 'SS2',
      },
      {
        q: 'Mercury depressed in glass tube because:',
        o: [
          'Mercury is denser',
          'Cohesive forces (mercury-mercury) > adhesive forces (mercury-glass)',
          'Mercury has higher surface tension',
          'Atmospheric pressure',
        ],
        a: 1,
        e: 'Mercury: cohesion>adhesion → depressed meniscus (convex). Opposite to water.',
        h: 'Mercury: cohesion>adhesion → depressed meniscus.',
        yr: 'SS2',
      },
      {
        q: 'Viscosity is:',
        o: [
          'Fluid density',
          "Fluid's resistance to flow (internal friction)",
          'Surface tension',
          'Compressibility',
        ],
        a: 1,
        e: 'Viscosity η = resistance to flow. Honey high η; water low η.',
        h: 'Viscosity = resistance to flow.',
        yr: 'SS2',
      },
      {
        q: "Pascal's Principle: pressure applied to enclosed fluid is:",
        o: [
          'Absorbed by container',
          'Transmitted equally in all directions',
          'Concentrated at bottom',
          'Lost as heat',
        ],
        a: 1,
        e: 'Pascal: pressure transmitted undiminished to all parts of enclosed fluid. F₁/A₁=F₂/A₂.',
        h: 'Pascal: pressure transmitted equally in all directions.',
        yr: 'SS2',
      },
      {
        q: 'Hydraulic press: effort piston area 2 cm², load piston area 20 cm², effort=100 N. Load?',
        o: ['10 N', '1000 N', '200 N', '50 N'],
        a: 1,
        e: 'F₁/A₁=F₂/A₂ → 100/2=F₂/20 → F₂=1000 N.',
        h: 'F₂=100×20/2=1000 N.',
        yr: 'SS2',
      },
      {
        q: 'Pressure at depth h in fluid of density ρ:',
        o: ['ρg/h', 'ρgh', 'gh/ρ', 'ρ/gh'],
        a: 1,
        e: 'P=ρgh. Increases linearly with depth.',
        h: 'Fluid pressure P=ρgh.',
        yr: 'SS2',
      },
      {
        q: 'Terminal velocity reached when:',
        o: [
          'Minimum velocity',
          'Zero acceleration: weight = upthrust + viscous drag',
          'Maximum acceleration',
          'Zero weight',
        ],
        a: 1,
        e: 'Terminal velocity: weight=upthrust+drag → net force=0 → constant velocity.',
        h: 'Terminal velocity: weight=upthrust+drag. Net force=0.',
        yr: 'SS2',
      },
      {
        q: "Stokes' Law: viscous drag on sphere =",
        o: ['½ρAv²', '6πηrv', 'ρgh', 'ma'],
        a: 1,
        e: "Stokes' Law: F=6πηrv. For slow laminar flow around a sphere.",
        h: "Stokes' Law: F=6πηrv.",
        yr: 'SS2',
      },
      {
        q: 'Elastic limit:',
        o: [
          'Point of fracture',
          'Max stress before permanent deformation',
          "Stress where Young's Modulus measured",
          'Stress=strain point',
        ],
        a: 1,
        e: "Elastic limit: max stress before permanent (plastic) deformation. Beyond it: body doesn't recover original shape.",
        h: 'Elastic limit = max stress before permanent deformation.',
        yr: 'SS2',
      },
      {
        q: 'Wire L=2 m, A=1 mm², stretched 1 mm under 100 N. E=?',
        o: ['2×10⁸ Pa', '2×10¹¹ Pa', '2×10⁵ Pa', '2×10² Pa'],
        a: 1,
        e: 'E=FL/(Ae)=100×2/(10⁻⁶×10⁻³)=200/10⁻⁹=2×10¹¹ Pa.',
        h: 'E=100×2/(10⁻⁶×10⁻³)=2×10¹¹ Pa.',
        yr: 'SS2',
      },
      {
        q: 'NOT a surface tension phenomenon:',
        o: [
          'Spherical water droplets',
          'Stable soap films',
          'Steel needle floating on water',
          "Mercury's viscosity higher than water's",
        ],
        a: 3,
        e: 'Spherical droplets, soap films, needle floating = surface tension. Mercury viscosity ≠ surface tension (different property).',
        h: 'Spherical drops, soap films, floating needle = surface tension. Viscosity is different.',
        yr: 'SS2',
      },
      {
        q: "Area under F-extension graph (Hooke's Law) represents:",
        o: ['Spring constant k', "Young's modulus", 'Elastic PE = ½Fe = ½ke²', 'Power delivered'],
        a: 2,
        e: 'Area under F-e graph = ½Fe = ½ke² = elastic PE stored.',
        h: 'Area under F-e graph = ½ke² = elastic PE.',
        yr: 'SS2',
      },
      {
        q: 'Hydraulic jack: effort piston A=5 cm², load piston A=200 cm². Lift 10,000 N car. Force needed?',
        o: ['250 N', '2000 N', '400 N', '500 N'],
        a: 0,
        e: 'F₁/A₁=F₂/A₂ → F₁=10000×5/200=250 N.',
        h: 'F₁=10000×5/200=250 N.',
        yr: 'SS2',
      },
      {
        q: 'Capillary rise greater in narrower tube because:',
        o: [
          'Lower pressure',
          'Weight decreases faster than upward force with decreasing radius',
          'Water more viscous in narrow tubes',
          'Surface tension increases',
        ],
        a: 1,
        e: 'Capillary rise h=2γcosθ/(ρgr). Narrower r → greater h. Weight (∝r²) decreases faster than upward force (∝r).',
        h: 'h∝1/r. Narrower tube → greater capillary rise.',
        yr: 'SS2',
      },
      {
        q: 'Bulk modulus of gas much lower than solid because:',
        o: [
          'Gases lighter',
          'Gases highly compressible — large ΔV for small P → low bulk modulus',
          'Gases lower density',
          'Solids always more elastic',
        ],
        a: 1,
        e: 'Bulk modulus K=−P/(ΔV/V). Gases easily compressed (large ΔV) → low K. Solids: large P → tiny ΔV → high K.',
        h: 'Gas: easily compressed → low bulk modulus. Solid: resists compression → high.',
        yr: 'SS2',
      },
      {
        q: "Archimedes' Principle: upthrust =",
        o: [
          'Weight of fluid displaced',
          'Volume of fluid displaced',
          'Mass of body',
          'Density of fluid × g',
        ],
        a: 0,
        e: 'Upthrust = weight of fluid displaced = ρ_fluid × g × V.',
        h: 'Archimedes: upthrust = weight of fluid displaced.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Heat Energy',
    topicCode: 'SS2-PHY-08',
    module: 'Thermal Physics',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro">Q=mcΔT (sensible heat). Q=mL (latent heat, no T change). K=°C+273. Conduction (solid), convection (fluid), radiation (no medium). ΔL=αL₀ΔT.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Q=mcΔT. Q=mL. K=°C+273. Radiation needs no medium. Conduction: solids via electrons/vibration. Convection: fluid density differences. ΔL=αL₀ΔT.</div>',
    questions: [
      {
        q: 'Heat needed to raise temperature:',
        o: ['Q=mL', 'Q=mcΔT', 'Q=mc/ΔT', 'Q=m/cΔT'],
        a: 1,
        e: 'Q=mcΔT (sensible heat). Q=mL for phase change at constant T.',
        h: 'Q=mcΔT (sensible heat).',
        yr: 'SS2',
      },
      {
        q: '2 kg water from 20°C to 70°C (c=4200 J/kg·K). Heat?',
        o: ['84 J', '4200 J', '420,000 J', '42,000 J'],
        a: 2,
        e: 'Q=mcΔT=2×4200×50=420,000 J.',
        h: 'Q=2×4200×50=420,000 J.',
        yr: 'SS2',
      },
      {
        q: 'Latent heat: heat absorbed or released during:',
        o: [
          'Temperature rise',
          'Change of state at constant temperature',
          'Chemical reaction',
          'Gas expansion',
        ],
        a: 1,
        e: 'Latent heat: phase change at constant T. Q=mL.',
        h: 'Latent heat = heat for phase change at constant T.',
        yr: 'SS2',
      },
      {
        q: 'Celsius to Kelvin:',
        o: ['K=°C−273', 'K=°C+273', 'K=°C×273', 'K=°C/273'],
        a: 1,
        e: 'K=°C+273. Absolute zero=0 K=−273°C.',
        h: 'K=°C+273.',
        yr: 'SS2',
      },
      {
        q: 'Heat transfer requiring NO medium:',
        o: ['Conduction', 'Convection', 'Both', 'Radiation'],
        a: 3,
        e: 'Radiation travels by EM waves through vacuum. Conduction and convection need a medium.',
        h: 'Radiation = no medium needed.',
        yr: 'SS2',
      },
      {
        q: '0.5 kg ice at 0°C melted by 168,000 J. Specific latent heat of fusion?',
        o: ['84,000 J/kg', '168,000 J/kg', '336,000 J/kg', '672,000 J/kg'],
        a: 2,
        e: 'L_f=Q/m=168000/0.5=336,000 J/kg.',
        h: 'L_f=168000/0.5=336,000 J/kg.',
        yr: 'SS2',
      },
      {
        q: 'Convection in fluids: heated fluid rises because:',
        o: [
          'Heat travels faster in fluids',
          'Heated fluid expands → less dense → rises; cooler fluid sinks → convection currents',
          'Radiation heats uniformly',
          'Conduction too slow',
        ],
        a: 1,
        e: 'Convection: hot fluid expands, less dense, rises. Cool fluid sinks. Creates convection currents.',
        h: 'Convection: hot fluid rises (less dense), cool sinks → convection currents.',
        yr: 'SS2',
      },
      {
        q: 'Steel rod L=1 m at 20°C, α=1.2×10⁻⁵/°C, T rises to 120°C. Extension?',
        o: ['1.2×10⁻³ m', '1.2×10⁻⁴ m', '1.2×10⁻⁵ m', '1.2×10⁻² m'],
        a: 0,
        e: 'ΔL=αL₀ΔT=1.2×10⁻⁵×1×100=1.2×10⁻³ m.',
        h: 'ΔL=1.2×10⁻⁵×100=1.2×10⁻³ m.',
        yr: 'SS2',
      },
      {
        q: 'During boiling, temperature stays at 100°C because:',
        o: [
          'Heat lost to atmosphere',
          'Energy used to overcome intermolecular forces (latent heat of vaporisation)',
          'Thermometer inaccurate',
          'Steam absorbs heat',
        ],
        a: 1,
        e: 'At boiling point, heat provides latent heat of vaporisation — breaks intermolecular bonds. Temperature constant until all liquid vaporised.',
        h: 'Boiling at constant 100°C: heat → latent heat of vaporisation.',
        yr: 'SS2',
      },
      {
        q: 'Good conductors of heat also conduct electricity because:',
        o: [
          'Both are liquids',
          'Free electrons carry both thermal energy AND electric charge',
          'Both are transparent',
          'Same density',
        ],
        a: 1,
        e: 'Metals: free electrons carry both heat (conduction) and charge (electricity).',
        h: 'Metals: free electrons carry heat AND charge.',
        yr: 'SS2',
      },
      {
        q: 'Mercury thermometer uses:',
        o: [
          'Resistance change',
          'Thermoelectric EMF',
          'Thermal expansion of liquid mercury',
          'Mercury vapour pressure change',
        ],
        a: 2,
        e: 'Mercury-in-glass: thermal expansion of mercury. Length of mercury column reads temperature.',
        h: 'Mercury thermometer: thermal expansion of liquid mercury.',
        yr: 'SS2',
      },
      {
        q: 'L_v >> L_f because:',
        o: [
          'Vaporisation at higher T',
          'Vaporisation requires complete separation of molecules (all intermolecular bonds broken); fusion only partially separates',
          'Fusion more common',
          'Vaporisation more mass',
        ],
        a: 1,
        e: 'L_v>>L_f: vaporisation breaks ALL bonds (molecules far apart as gas); fusion only partially separates (solid→liquid).',
        h: 'L_v>>L_f: vaporisation requires complete molecular separation.',
        yr: 'SS2',
      },
      {
        q: 'Metal 100g at 100°C in 200g water at 20°C; final T=30°C; c_water=4200. c_metal=?',
        o: ['840 J/kg·K', '4200 J/kg·K', '1200 J/kg·K', '2100 J/kg·K'],
        a: 2,
        e: '0.1×c_m×70=0.2×4200×10=8400 → c_m=8400/7=1200 J/kg·K.',
        h: 'm_m×c_m×ΔT_m=m_w×c_w×ΔT_w → c_m=1200 J/kg·K.',
        yr: 'SS2',
      },
      {
        q: 'Greenhouse effect: greenhouse gases warm Earth by:',
        o: [
          'Conducting heat into atmosphere',
          'Being transparent to outgoing IR',
          "Absorbing Earth's outgoing IR and re-radiating back to Earth",
          'Blocking convection',
        ],
        a: 2,
        e: "GHGs absorb Earth's outgoing long-wavelength IR and re-radiate in all directions, including back to surface → warming.",
        h: 'Greenhouse: GHGs absorb outgoing IR → re-radiate back → warming.',
        yr: 'SS2',
      },
      {
        q: 'Land breeze at night because:',
        o: [
          'Sea warmer than land at night',
          'Land cools faster → cool air sinks over land → breeze from land to sea',
          'Sea rises at night',
          'Land has higher specific heat capacity',
        ],
        a: 1,
        e: 'Night: land cools faster (lower specific heat capacity) → cool air sinks over land → breeze from land toward sea.',
        h: 'Night: land cools faster → air sinks → land to sea breeze.',
        yr: 'SS2',
      },
      {
        q: 'Vacuum flask minimises heat by:',
        o: [
          'Thick plastic walls',
          'Double silvered walls (radiation), vacuum (no conduction/convection), plastic stopper',
          'Cotton wool filling',
          'Small opening only',
        ],
        a: 1,
        e: 'Vacuum flask: vacuum eliminates conduction/convection; silvered walls reflect IR (radiation); plastic stopper insulates.',
        h: 'Vacuum flask: vacuum + silvered walls + plastic stopper.',
        yr: 'SS2',
      },
      {
        q: 'Evaporative cooling works because:',
        o: [
          'Evaporation adds heat to liquid',
          'Most energetic molecules escape → average KE of remaining liquid decreases → cools',
          'Evaporation compresses liquid',
          'Cold air above cools liquid',
        ],
        a: 1,
        e: 'Evaporation: fastest molecules escape → average KE of remaining liquid falls → temperature drops.',
        h: 'Evaporative cooling: fast molecules escape → average KE drops → liquid cools.',
        yr: 'SS2',
      },
      {
        q: 'Volume coefficient γ related to linear α:',
        o: ['γ=α', 'γ=2α', 'γ=3α (isotropic)', 'γ=α/3'],
        a: 2,
        e: 'γ=3α for isotropic solid (equal expansion in all three dimensions).',
        h: 'γ=3α (volume); 2α (area); α (linear).',
        yr: 'SS2',
      },
      {
        q: 'Clinical thermometer constriction purpose:',
        o: [
          'More accurate reading',
          'Mercury stays in tube after removal — prevents return before reading',
          'Reduces mercury volume',
          'Increases range',
        ],
        a: 1,
        e: 'Constriction prevents mercury flowing back into bulb after removal from patient — preserving the reading.',
        h: 'Constriction: prevents mercury returning to bulb → reading preserved.',
        yr: 'SS2',
      },
      {
        q: 'Specific heat capacity c means:',
        o: [
          'Substance boils at c degrees',
          'c J needed per gram per degree Celsius temperature rise',
          'Heat released when freezing',
          'Thermal conductivity',
        ],
        a: 1,
        e: 'c = heat per unit mass per degree temperature change. Q=mcΔT.',
        h: 'c = J per kg per K. Used in Q=mcΔT.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Waves',
    topicCode: 'SS2-PHY-09',
    module: 'Waves',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro">v=fλ. Transverse: oscillation ⊥ propagation (light, water waves). Longitudinal: oscillation ∥ propagation (sound). Reflection, refraction, diffraction, interference. Diffraction greatest when gap≈λ.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> v=fλ. T=1/f. Transverse (light). Longitudinal (sound). Constructive: crest+crest. Destructive: crest+trough. Sound in air ≈340 m/s. Fringe spacing Δy=λD/d.</div>',
    questions: [
      {
        q: 'Transverse differs from longitudinal wave: particles oscillate:',
        o: [
          'Faster',
          'Perpendicular (⊥) to direction of propagation',
          'Parallel to direction of propagation',
          'In circles',
        ],
        a: 1,
        e: 'Transverse: oscillation ⊥ propagation (light, water waves). Longitudinal: oscillation ∥ propagation (sound).',
        h: 'Transverse: oscillation ⊥ propagation.',
        yr: 'SS2',
      },
      {
        q: 'Wave equation:',
        o: ['v=f+λ', 'v=fλ', 'v=f/λ', 'v=λ/f'],
        a: 1,
        e: 'v=fλ. Speed = frequency × wavelength.',
        h: 'Wave equation: v=fλ.',
        yr: 'SS2',
      },
      {
        q: 'Wave: f=200 Hz, λ=1.5 m. Speed?',
        o: ['133 m/s', '300 m/s', '200 m/s', '0.0075 m/s'],
        a: 1,
        e: 'v=fλ=200×1.5=300 m/s.',
        h: 'v=200×1.5=300 m/s.',
        yr: 'SS2',
      },
      {
        q: 'Sound waves are longitudinal because:',
        o: [
          'Travel in vacuum',
          'Air molecules vibrate parallel to wave direction (compressions/rarefactions)',
          'Cannot be reflected',
          'Longer wavelength than light',
        ],
        a: 1,
        e: 'Sound: air molecules compress/rarefy parallel to propagation — longitudinal wave.',
        h: 'Sound = longitudinal: air molecules vibrate parallel to propagation.',
        yr: 'SS2',
      },
      {
        q: 'Diffraction most noticeable when:',
        o: ['Gap >> wavelength', 'Gap << wavelength', 'Gap ≈ wavelength', 'Frequency very high'],
        a: 2,
        e: 'Diffraction greatest when gap size ≈ wavelength.',
        h: 'Maximum diffraction: gap ≈ wavelength.',
        yr: 'SS2',
      },
      {
        q: 'Constructive interference:',
        o: [
          'Waves cancel',
          'Crest meets trough',
          'Crests coincide (in phase) — amplitude doubled',
          'One amplitude larger',
        ],
        a: 2,
        e: 'Constructive: in phase (crest+crest) → amplitude = A₁+A₂.',
        h: 'Constructive: in phase (crest+crest) → amplitude adds.',
        yr: 'SS2',
      },
      {
        q: 'Period of wave with f=50 Hz:',
        o: ['50 s', '0.02 s', '5 s', '0.5 s'],
        a: 1,
        e: 'T=1/f=1/50=0.02 s.',
        h: 'T=1/f=0.02 s.',
        yr: 'SS2',
      },
      {
        q: 'Speed of sound in air at room temperature ≈',
        o: ['3×10⁸ m/s', '340 m/s', '1500 m/s', '17 m/s'],
        a: 1,
        e: 'Speed of sound in air ≈ 340 m/s at 20°C.',
        h: 'Speed of sound in air ≈ 340 m/s.',
        yr: 'SS2',
      },
      {
        q: 'Refraction occurs because:',
        o: [
          'Waves bounce at boundary',
          'Waves change speed at boundary → change direction',
          'Waves spread after gap',
          'Waves superpose at boundary',
        ],
        a: 1,
        e: 'Refraction: speed changes at boundary → wave bends. f unchanged; λ changes.',
        h: 'Refraction: speed change at boundary → change direction.',
        yr: 'SS2',
      },
      {
        q: 'Which requires a material medium?',
        o: ['X-rays', 'Radio waves', 'Sound waves', 'Gamma rays'],
        a: 2,
        e: 'Sound = mechanical wave; needs medium. EM waves (radio, X-rays) travel through vacuum.',
        h: 'Sound = mechanical wave; needs medium.',
        yr: 'SS2',
      },
      {
        q: 'Amplitude determines:',
        o: ['Frequency', 'Wavelength', 'Energy and loudness/brightness', 'Speed'],
        a: 2,
        e: 'Amplitude determines energy (∝A²). Larger A = louder sound or brighter light.',
        h: 'Amplitude → energy, loudness (sound), brightness (light).',
        yr: 'SS2',
      },
      {
        q: 'Echo produced by:',
        o: ['Refraction', 'Reflection of sound from hard surface', 'Diffraction', 'Interference'],
        a: 1,
        e: 'Echo = reflection of sound from a hard distant surface.',
        h: 'Echo = reflection of sound.',
        yr: 'SS2',
      },
      {
        q: "Fringe spacing in Young's double slit increases when:",
        o: [
          'Slit separation increases',
          'Wavelength decreases',
          'Slit-screen distance increases',
          'Light frequency increases',
        ],
        a: 2,
        e: 'Δy=λD/d. Increasing D increases Δy.',
        h: 'Fringe spacing Δy=λD/d. Increases with screen distance D.',
        yr: 'SS2',
      },
      {
        q: 'Destructive interference:',
        o: [
          'Waves exactly in phase',
          'Waves exactly out of phase (crest meets trough) — cancellation',
          'One amplitude doubled',
          'Two different frequencies',
        ],
        a: 1,
        e: 'Destructive: out of phase 180° (crest+trough) → amplitude = |A₁−A₂|.',
        h: 'Destructive: out of phase (crest+trough) → amplitudes cancel.',
        yr: 'SS2',
      },
      {
        q: 'Wavelength of 680 Hz sound in air (v=340 m/s)?',
        o: ['0.2 m', '0.5 m', '2 m', '5 m'],
        a: 1,
        e: 'λ=v/f=340/680=0.5 m.',
        h: 'λ=v/f=340/680=0.5 m.',
        yr: 'SS2',
      },
      {
        q: 'Standing waves differ from progressive waves:',
        o: [
          'Transfer energy faster',
          'Fixed nodes and antinodes; no net energy transfer along wave',
          'Greater amplitude',
          'Only in gases',
        ],
        a: 1,
        e: 'Standing waves: fixed nodes/antinodes; no net energy transfer. KE↔PE at fixed locations.',
        h: 'Standing waves: fixed nodes/antinodes; no net energy transfer.',
        yr: 'SS2',
      },
      {
        q: 'Doppler effect: approaching source causes:',
        o: [
          'Lower frequency',
          'Higher observed frequency',
          'No frequency change',
          'Lower amplitude',
        ],
        a: 1,
        e: 'Doppler: approaching source → wavefronts compressed → shorter λ → higher f.',
        h: 'Doppler: approaching source → higher observed frequency.',
        yr: 'SS2',
      },
      {
        q: 'Pitch corresponds to:',
        o: [
          'Amplitude',
          'Speed',
          'Frequency — higher f = higher pitch',
          'Wavelength — longer = higher pitch',
        ],
        a: 2,
        e: 'Pitch ∝ frequency. High f = high pitch. Loudness ∝ amplitude.',
        h: 'Pitch ∝ frequency. Loudness ∝ amplitude.',
        yr: 'SS2',
      },
      {
        q: 'Echo heard 0.6 s later (v=340 m/s). Distance to cliff?',
        o: ['204 m', '102 m', '340 m', '408 m'],
        a: 1,
        e: 'Distance = v×t/2 = 340×0.6/2 = 102 m.',
        h: 'Distance = 340×0.6/2 = 102 m.',
        yr: 'SS2',
      },
      {
        q: 'Which of these are transverse waves?',
        o: ['Sound waves', 'Seismic P-waves', 'Light waves', 'All mechanical waves'],
        a: 2,
        e: 'Light (EM waves) are transverse. Sound and seismic P-waves are longitudinal. Seismic S-waves are transverse.',
        h: 'Light = transverse. Sound = longitudinal.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Electromagnetic Waves',
    topicCode: 'SS2-PHY-10',
    module: 'Waves',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro">EM waves: transverse, travel at c=3×10⁸ m/s in vacuum, no medium needed. Order (decreasing λ): Radio→Microwave→IR→Visible→UV→X-ray→Gamma. E=hf.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> All EM: c=3×10⁸ m/s; transverse; no medium. Radio (longest λ) to Gamma (shortest λ). E=hf: higher f → more energy per photon. Memory: R-M-I-V-U-X-G.</div>',
    questions: [
      {
        q: 'Common property of all EM waves:',
        o: [
          'Same wavelength',
          'Require medium',
          'Speed c=3×10⁸ m/s in vacuum; transverse',
          'Same frequency',
        ],
        a: 2,
        e: 'All EM: c=3×10⁸ m/s; transverse; no medium needed; carry energy.',
        h: 'All EM: c=3×10⁸ m/s, transverse, no medium needed.',
        yr: 'SS2',
      },
      {
        q: 'Longest wavelength in EM spectrum:',
        o: ['Gamma', 'X-rays', 'Visible', 'Radio waves'],
        a: 3,
        e: 'Order: Radio (longest) → Microwave → IR → Visible → UV → X-ray → Gamma (shortest).',
        h: 'Longest λ: Radio waves.',
        yr: 'SS2',
      },
      {
        q: 'Infrared used in remote controls because:',
        o: [
          'Visible and aimed',
          'Penetrates walls',
          'Signals carried by cheap LEDs, invisible, safe, easily detected',
          'Fastest EM wave',
        ],
        a: 2,
        e: 'IR: cheap LEDs, invisible, safe, easily detected by sensors in TVs.',
        h: 'IR remote controls: cheap, safe, invisible, easily detected.',
        yr: 'SS2',
      },
      {
        q: 'X-rays used in medical imaging because:',
        o: [
          'Same wavelength as visible',
          'Penetrate soft tissue; absorbed by bone → shadow image',
          'Absorbed by all tissues',
          'Travel faster',
        ],
        a: 1,
        e: 'X-rays penetrate soft tissue; absorbed by dense bone → creates shadow image on detector.',
        h: 'X-rays: penetrate soft tissue; bone absorbs → medical imaging.',
        yr: 'SS2',
      },
      {
        q: 'Gamma vs X-rays: difference is:',
        o: [
          'Longer wavelength',
          'Transverse vs longitudinal',
          'Origin: gamma from nuclear decay; X-rays from electron transitions/deceleration',
          'Different speeds in vacuum',
        ],
        a: 2,
        e: 'Gamma: nuclear decay. X-rays: electron deceleration or inner-shell transitions. Both travel at c.',
        h: 'Gamma from nucleus; X-rays from electrons. Both travel at c.',
        yr: 'SS2',
      },
      {
        q: 'UV causes sunburn because:',
        o: [
          'Visible and bright',
          'UV photons energetic enough to damage DNA and skin cells',
          'Heats skin more than visible',
          'Reflected by skin',
        ],
        a: 1,
        e: 'UV photons (E=hf, high f) carry enough energy to break chemical bonds in DNA → sunburn, cell damage.',
        h: 'UV: photons damage DNA and skin cells → sunburn.',
        yr: 'SS2',
      },
      {
        q: 'Microwave ovens cook using:',
        o: ['Infrared', 'Radio waves', 'Microwaves (~2.45 GHz)', 'UV'],
        a: 2,
        e: 'Microwave ovens: 2.45 GHz microwaves absorbed efficiently by water molecules → food heats.',
        h: 'Microwave ovens: 2.45 GHz, absorbed by water molecules.',
        yr: 'SS2',
      },
      {
        q: 'Light slower in glass because:',
        o: [
          'Glass absorbs photons',
          'Photons interact with glass atoms → slowed propagation (n=c/v>1)',
          'Glass is magnetic',
          'Light changes frequency in glass',
        ],
        a: 1,
        e: 'In glass: photon-atom interaction slows effective propagation. n=c/v>1. Frequency unchanged; wavelength decreases.',
        h: 'Light slower in glass: n=c/v>1. f unchanged; λ decreases.',
        yr: 'SS2',
      },
      {
        q: 'Gamma rays used in cancer treatment (radiotherapy) because:',
        o: [
          'Visible, targets tumours',
          'Highly penetrating; energetic enough to destroy tumour DNA',
          'Reflects off cancer cells only',
          'Same as X-rays',
        ],
        a: 1,
        e: 'Gamma: highly penetrating; destroys tumour DNA. Focused beams concentrate dose at tumour.',
        h: 'Gamma radiotherapy: penetrating; destroys tumour DNA.',
        yr: 'SS2',
      },
      {
        q: 'Nature of EM waves:',
        o: [
          'Longitudinal pressure variations',
          'Transverse with perpendicular oscillating E and B fields',
          'Mechanical requiring particle vibration',
          'Travel only in straight lines',
        ],
        a: 1,
        e: 'EM waves: oscillating E⊥B⊥direction. Transverse. No medium. Travel at c.',
        h: 'EM: transverse; E⊥B⊥direction; c=3×10⁸ m/s; no medium.',
        yr: 'SS2',
      },
      {
        q: 'E=hf means:',
        o: [
          'Lower f → more energy per photon',
          'Higher f (shorter λ) → more energy per photon',
          'Energy independent of frequency',
          'All photons same energy',
        ],
        a: 1,
        e: 'E=hf. Higher f → higher energy per photon. Gamma > X-ray > UV > visible > IR > radio.',
        h: 'E=hf: higher f → higher energy per photon.',
        yr: 'SS2',
      },
      {
        q: 'Optical fibres work by:',
        o: [
          'Diffraction',
          'Total internal reflection',
          'Constructive interference',
          'Polarisation',
        ],
        a: 1,
        e: 'Optical fibres: TIR keeps light within the glass core over long distances.',
        h: 'Optical fibres: total internal reflection.',
        yr: 'SS2',
      },
      {
        q: 'Radar uses:',
        o: ['Gamma', 'Infrared', 'Microwaves', 'Very long radio waves'],
        a: 2,
        e: 'Radar uses microwaves that reflect off metal objects. Time delay gives distance.',
        h: 'Radar = microwaves.',
        yr: 'SS2',
      },
      {
        q: 'Human body at 37°C emits primarily:',
        o: ['Visible light', 'Ultraviolet', 'Infrared radiation', 'Microwaves'],
        a: 2,
        e: 'Objects at 37°C (310 K) emit IR radiation (peak ~10 μm). Used in thermal cameras.',
        h: 'Human body at 37°C emits infrared.',
        yr: 'SS2',
      },
      {
        q: 'Sky appears blue because:',
        o: [
          'Sea reflects blue',
          'Rayleigh scattering: blue (shorter λ) scattered much more than red',
          'Sun emits mostly blue',
          'Clouds absorb red',
        ],
        a: 1,
        e: 'Rayleigh scattering ∝ 1/λ⁴. Blue light scattered most → sky appears blue.',
        h: 'Blue sky: Rayleigh scattering; blue scattered most (∝1/λ⁴).',
        yr: 'SS2',
      },
      {
        q: 'Communication satellites use:',
        o: ['Visible light', 'X-rays', 'Microwaves and radio waves', 'Gamma'],
        a: 2,
        e: 'Communication satellites use microwaves (4-40 GHz) and radio waves.',
        h: 'Communication satellites: microwaves and radio waves.',
        yr: 'SS2',
      },
      {
        q: 'Polarisation is property of:',
        o: [
          'Longitudinal waves only',
          'Both longitudinal and transverse',
          'Transverse waves only',
          'EM and sound equally',
        ],
        a: 2,
        e: 'Polarisation: transverse waves only. Sound (longitudinal) cannot be polarised.',
        h: 'Polarisation: transverse waves only (not longitudinal sound).',
        yr: 'SS2',
      },
      {
        q: 'Critical angle: refracted ray is at:',
        o: [
          'All light transmitted',
          'Exactly 90° to normal (along boundary)',
          'All light absorbed',
          'Reflection = refraction',
        ],
        a: 1,
        e: 'Critical angle: refracted ray at 90° to normal (grazes boundary). Above this: TIR.',
        h: 'Critical angle: refracted ray at 90° to normal.',
        yr: 'SS2',
      },
      {
        q: 'Microwaves can cause biological harm by:',
        o: [
          'Damaging DNA like gamma',
          'Heating water molecules in tissue — internal tissue damage at high power',
          'Blocking cell functions',
          'Immediate cell death at any power',
        ],
        a: 1,
        e: 'Microwaves: heat water molecules in tissue. High power → internal burns.',
        h: 'Microwaves: heat water molecules → tissue damage at high power.',
        yr: 'SS2',
      },
      {
        q: 'TIR requires:',
        o: [
          'Light passing less dense to denser medium',
          'Light at any angle in any medium',
          'Light in denser medium at angle > critical angle',
          'Light polarised at boundary',
        ],
        a: 2,
        e: 'TIR: light in denser medium (n₁>n₂); angle of incidence > critical angle → all reflected.',
        h: 'TIR: denser medium; angle > critical angle → all light reflected.',
        yr: 'SS2',
      },
    ],
  },
  // =========================================================================
  // TOPIC 11: OPTICS (LIGHT)
  // =========================================================================
  {
    topic: 'Optics (Light)',
    topicCode: 'SS2-PHY-11',
    module: 'Optics',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Optics</span> is the study of light and its interactions with matter. It covers reflection by mirrors, refraction through lenses and prisms, and the formation of images — essential for understanding cameras, spectacles, telescopes, and the human eye.</div><h3 class="learn-subheading">1. Reflection at Plane Mirrors</h3><ul class="learn-list"><li>Laws: angle of incidence = angle of reflection; incident ray, normal, reflected ray are coplanar</li><li>Image in plane mirror: virtual, upright, same size, laterally inverted, same distance behind mirror</li></ul><h3 class="learn-subheading">2. Curved Mirrors</h3><ul class="learn-list"><li><strong>Concave mirror:</strong> converging; focal point F in front of mirror. Used in: headlights, solar concentrators, dentist mirrors</li><li><strong>Convex mirror:</strong> diverging; focal point F behind mirror. Used in: rear-view mirrors, shop security mirrors — wide field of view</li><li>Mirror formula: 1/f = 1/v + 1/u; magnification m = v/u</li></ul><h3 class="learn-subheading">3. Refraction and Snell\'s Law</h3><p class="learn-p">n₁ sin θ₁ = n₂ sin θ₂ (Snell\'s Law). Refractive index n = c/v = sin i / sin r. Light bends toward the normal when entering a denser medium (lower speed).</p><h3 class="learn-subheading">4. Lenses</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Lens</th><th>Type</th><th>Image</th><th>Uses</th></tr></thead><tbody><tr><td>Converging (convex)</td><td>Thicker at centre</td><td>Real and inverted (object beyond F); virtual and upright (object within F)</td><td>Camera, projector, magnifying glass, eye</td></tr><tr><td>Diverging (concave)</td><td>Thinner at centre</td><td>Always virtual, upright, diminished</td><td>Correcting short-sightedness (myopia)</td></tr></tbody></table></div><p class="learn-p">Lens formula: 1/f = 1/v − 1/u. Power of lens: P = 1/f (unit: dioptre, D). Magnification: m = v/u.</p><h3 class="learn-subheading">5. Defects of Vision</h3><ul class="learn-list"><li><strong>Myopia (short-sightedness):</strong> distant objects blurred; image forms in front of retina. Corrected by diverging (concave) lens.</li><li><strong>Hypermetropia (long-sightedness):</strong> near objects blurred; image would form behind retina. Corrected by converging (convex) lens.</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Sign Convention:</strong> Real is positive for image distance (v). Distances measured from optical centre. Lens formula: 1/f = 1/v − 1/u. Mirror formula: 1/f = 1/v + 1/u.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Snell: n₁sinθ₁=n₂sinθ₂. Converging lens/concave mirror = real image (object beyond F). Myopia → concave lens. Hypermetropia → convex lens. Power = 1/f (D).</div>',
    questions: [
      {
        q: 'The laws of reflection state that:',
        o: [
          'The angle of incidence equals twice the angle of reflection',
          'The angle of incidence equals the angle of reflection; the incident ray, normal, and reflected ray all lie in the same plane',
          'Light always reflects at 90°',
          'The reflected ray is always parallel to the incident ray',
        ],
        a: 1,
        e: 'Laws of reflection: (1) angle of incidence (i) = angle of reflection (r), both measured from the normal; (2) incident ray, normal, and reflected ray are all coplanar (in the same plane). These apply to both plane and curved mirrors.',
        h: 'Reflection laws: i = r; incident ray, normal, reflected ray coplanar.',
        yr: 'SS2',
      },
      {
        q: 'An object 6 cm tall stands 30 cm in front of a concave mirror of focal length 20 cm. Using 1/f = 1/v + 1/u, the image distance v is:',
        o: ['12 cm', '60 cm', '−60 cm', '−12 cm'],
        a: 1,
        e: '1/f = 1/v + 1/u → 1/20 = 1/v + 1/30 → 1/v = 1/20 − 1/30 = 3/60 − 2/60 = 1/60 → v = 60 cm. (Positive v = real image in front of mirror for concave.)',
        h: '1/20 = 1/v + 1/30 → 1/v = 1/60 → v = 60 cm.',
        yr: 'SS2',
      },
      {
        q: 'A convex mirror always produces an image that is:',
        o: [
          'Real, inverted, and magnified',
          'Virtual, upright, and diminished — regardless of object position',
          'Real, upright, and same size',
          'Virtual, inverted, and magnified',
        ],
        a: 1,
        e: 'A convex (diverging) mirror always produces a virtual, upright, diminished image regardless of object position. This gives a wide field of view — making convex mirrors ideal for rear-view mirrors and security/shop mirrors.',
        h: 'Convex mirror: always virtual, upright, diminished. Wide field of view.',
        yr: 'SS2',
      },
      {
        q: "Snell's Law states that:",
        o: [
          'n = c/v',
          'n₁ sin θ₁ = n₂ sin θ₂',
          'sin θ₁ / sin θ₂ = v₂/v₁ = n₂/n₁',
          "Both b and c are correct expressions of Snell's Law",
        ],
        a: 3,
        e: "Snell's Law: n₁sinθ₁ = n₂sinθ₂. Equivalently: sinθ₁/sinθ₂ = n₂/n₁ = v₁/v₂. Both options B and C are correct expressions of the same law. The correct answer encompasses both.",
        h: "Snell's Law: n₁sinθ₁ = n₂sinθ₂ (or sinθ₁/sinθ₂ = n₂/n₁).",
        yr: 'SS2',
      },
      {
        q: 'A ray of light passes from air (n=1) into glass (n=1.5) at an angle of incidence of 30°. The angle of refraction is:',
        o: ['45°', '30°', '19.5°', '48.6°'],
        a: 2,
        e: 'Snell: sinθ₂ = n₁sinθ₁/n₂ = 1×sin30°/1.5 = 0.5/1.5 = 0.333. θ₂ = arcsin(0.333) ≈ 19.5°. Light bends toward the normal when entering a denser medium (n₂ > n₁ → θ₂ < θ₁).',
        h: 'sinθ₂ = sin30°/1.5 = 0.333 → θ₂ ≈ 19.5°.',
        yr: 'SS2',
      },
      {
        q: 'The power of a lens with focal length 25 cm is:',
        o: ['25 D', '4 D', '0.25 D', '2.5 D'],
        a: 1,
        e: 'P = 1/f = 1/0.25 m = 4 D. (Convert focal length to metres: 25 cm = 0.25 m.) Converging lenses have positive power; diverging lenses have negative power.',
        h: 'P = 1/f = 1/0.25 = 4 D.',
        yr: 'SS2',
      },
      {
        q: 'A person with myopia (short-sightedness) cannot see distant objects clearly because:',
        o: [
          'The eyeball is too short',
          'The lens is too flat',
          'The image of distant objects forms in front of the retina (eyeball too long or lens too powerful)',
          'The cornea is transparent',
        ],
        a: 2,
        e: 'Myopia: distant object image focuses in front of the retina (before reaching it). Cause: eyeball too long axially or lens too powerful. Correction: diverging (concave) lens that diverges rays before they enter the eye, shifting the focal point back onto the retina.',
        h: 'Myopia: image in front of retina. Corrected by diverging (concave) lens.',
        yr: 'SS2',
      },
      {
        q: 'A converging lens is used as a magnifying glass when:',
        o: [
          'The object is placed beyond the focal point',
          'The object is placed between the focal point and the lens — producing a virtual, upright, magnified image',
          'The object is at the focal point',
          'The object is at twice the focal length',
        ],
        a: 1,
        e: 'Magnifying glass: object placed inside the focal length (u < f). The diverging rays from the lens appear to come from a virtual, upright, magnified image on the same side as the object. This is why you hold a magnifying glass close to the object.',
        h: 'Magnifying glass: object inside focal length (u < f) → virtual, upright, magnified image.',
        yr: 'SS2',
      },
      {
        q: 'The image formed by a plane mirror is:',
        o: [
          'Real, inverted, and the same size as the object',
          'Virtual, upright, laterally inverted, the same size, and as far behind the mirror as the object is in front',
          'Real, inverted, and magnified',
          'Virtual, upright, and magnified',
        ],
        a: 1,
        e: 'Plane mirror image: (1) virtual (cannot be projected on a screen); (2) upright (same orientation); (3) laterally inverted (left-right reversed); (4) same size as object; (5) image distance behind mirror = object distance in front.',
        h: 'Plane mirror: virtual, upright, laterally inverted, same size, same distance behind mirror.',
        yr: 'SS2',
      },
      {
        q: 'Total internal reflection occurs when:',
        o: [
          'Light passes from a less dense to a denser medium',
          'Light travels along the boundary between two media',
          'Light in a denser medium hits the boundary at an angle greater than the critical angle',
          'Light is polarised at the boundary',
        ],
        a: 2,
        e: 'TIR: light in a denser medium (n₁ > n₂) hits the boundary at angle θ > θ_c (critical angle). sinθ_c = n₂/n₁. All light is reflected back into the denser medium — none is refracted. Used in optical fibres, prism binoculars, and diamonds.',
        h: 'TIR: light in denser medium; angle > critical angle → all light reflected back.',
        yr: 'SS2',
      },
      {
        q: 'A camera lens forms images on the film/sensor. The image is:',
        o: [
          'Virtual, upright, and magnified',
          'Real, inverted, and diminished (for distant objects)',
          'Virtual, inverted, and the same size',
          'Real, upright, and magnified',
        ],
        a: 1,
        e: 'Camera: converging lens forms a real, inverted image on the sensor/film. For distant objects (u >> f), the image is at approximately the focal point (v ≈ f) and is much smaller than the object (diminished). Real images can be projected onto a screen/film.',
        h: 'Camera: converging lens → real, inverted, diminished image on sensor (for distant objects).',
        yr: 'SS2',
      },
      {
        q: 'The refractive index of glass relative to air is 1.5. The critical angle for total internal reflection at a glass-air interface is:',
        o: ['30°', '41.8°', '48.6°', '60°'],
        a: 1,
        e: 'sinθ_c = n₂/n₁ = 1/1.5 = 0.667. θ_c = arcsin(0.667) ≈ 41.8°. For angles > 41.8°, TIR occurs at the glass-air boundary.',
        h: 'sinθ_c = 1/n = 1/1.5 = 0.667 → θ_c ≈ 41.8°.',
        yr: 'SS2',
      },
      {
        q: 'Hypermetropia (long-sightedness) is corrected by:',
        o: [
          'A diverging (concave) lens',
          'A converging (convex) lens that converges rays before they enter the eye, allowing near objects to be focused on the retina',
          'Thicker spectacle lenses of any type',
          'Wearing a patch over one eye',
        ],
        a: 1,
        e: 'Hypermetropia: near object image would form behind the retina (eyeball too short or lens too weak). Correction: converging (convex) lens provides additional convergence, shifting the image forward onto the retina. This enables clear near vision.',
        h: 'Hypermetropia (long-sight): image behind retina. Corrected by converging (convex) lens.',
        yr: 'SS2',
      },
      {
        q: 'A prism disperses white light into a spectrum because:',
        o: [
          'Different colours travel at different speeds in the prism (different refractive indices), so they refract by different amounts',
          'All colours travel at the same speed in glass',
          'The prism is triangular',
          'Reflection separates colours',
        ],
        a: 0,
        e: 'White light dispersion: different wavelengths (colours) travel at slightly different speeds in glass → different refractive indices → different refraction angles. Violet light refracts most (highest n, lowest speed in glass); red refracts least (lowest n). Result: ROYGBIV spectrum.',
        h: 'Prism dispersion: different colours have different n → different refraction → spectrum (ROYGBIV).',
        yr: 'SS2',
      },
      {
        q: 'A concave mirror is used in a car headlight because:',
        o: [
          'It produces a wide field of view',
          'The light bulb is placed at the focal point, and the mirror reflects parallel rays of light forward (concentrating light into a beam)',
          'It magnifies all objects in front of it',
          'It is cheaper than other mirror types',
        ],
        a: 1,
        e: 'Car headlight: the bulb is at the focal point F of a concave mirror. Rays from F hit the mirror and are reflected as parallel rays (collimated beam) — projecting a strong, directed beam of light forward. This is the reverse of a concave mirror as a solar concentrator.',
        h: 'Headlight: bulb at focal point F → reflected as parallel beam.',
        yr: 'SS2',
      },
      {
        q: 'The magnification produced by a lens is defined as:',
        o: ['f/u', 'Image height/Object height = v/u', 'u×v', 'f/(u−f)'],
        a: 1,
        e: 'Linear magnification m = image height/object height = v/u (image distance/object distance). m > 1: magnified; m < 1: diminished; m = 1: same size. Negative m indicates inverted image (real).',
        h: 'Magnification m = image height/object height = v/u.',
        yr: 'SS2',
      },
      {
        q: 'Lateral inversion in a plane mirror means:',
        o: [
          'The image is upside down',
          'Left and right are swapped — left hand appears as right hand in the mirror',
          'The image is smaller',
          'The image is further away than the object',
        ],
        a: 1,
        e: 'Lateral inversion: left-right reversal in a plane mirror. When you raise your right hand, the image appears to raise its left hand. Text appears backwards in a mirror. This is why AMBULANCE is written in reverse on ambulance fronts — to appear correct in the rear-view mirror.',
        h: 'Lateral inversion = left-right reversal in plane mirror.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following does NOT affect the speed of light in a medium?',
        o: [
          'The refractive index of the medium',
          'The density and structure of the medium',
          'The frequency of the light',
          'The type of medium (glass, water, air)',
        ],
        a: 2,
        e: "The speed of light in a medium v = c/n depends on the refractive index n (which depends on the medium's properties). Frequency does NOT change when light moves from one medium to another — only speed and wavelength change. v = fλ (f constant, v changes → λ changes).",
        h: 'Speed in medium: v = c/n. Frequency does NOT change in different media (wavelength changes).',
        yr: 'SS2',
      },
      {
        q: 'An object is placed 40 cm from a converging lens of focal length 10 cm. The image distance v is:',
        o: ['13.3 cm', '40 cm', '−13.3 cm', '10 cm'],
        a: 0,
        e: '1/f = 1/v − 1/u. Using the convention where object distance u is negative for real objects: 1/10 = 1/v − 1/(−40). 1/10 = 1/v + 1/40. 1/v = 1/10 − 1/40 = 4/40 − 1/40 = 3/40. v = 40/3 ≈ 13.3 cm. (Real, inverted, diminished image since v > 0 and v < f... wait: v=13.3 cm > f=10 cm but u=40 > 2f=20 → diminished image.)',
        h: '1/f=1/v+1/u (real is positive): 1/10=1/v+1/40 → 1/v=3/40 → v=13.3 cm.',
        yr: 'SS2',
      },
      {
        q: 'A diverging lens always produces an image that is:',
        o: [
          'Real, inverted, and magnified',
          'Virtual, upright, and diminished — regardless of object position',
          'Real, upright, and the same size',
          'Virtual, inverted, and magnified',
        ],
        a: 1,
        e: 'A diverging (concave) lens always produces a virtual, upright, diminished image regardless of object distance. It is used to correct myopia (short-sightedness). The image is always on the same side as the object.',
        h: 'Diverging lens: always virtual, upright, diminished. Used to correct myopia.',
        yr: 'SS2',
      },
    ],
  },
  // =========================================================================
  // TOPIC 12: ELECTROSTATICS
  // =========================================================================
  {
    topic: 'Electrostatics',
    topicCode: 'SS2-PHY-12',
    module: 'Electricity',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Electrostatics</span> is the study of electric charges at rest. It covers the nature of electric charge, Coulomb\'s Law, electric fields, electric potential, and capacitance — the foundations of electrical engineering and electronics.</div><h3 class="learn-subheading">1. Electric Charge</h3><ul class="learn-list"><li>Two types: positive (protons) and negative (electrons)</li><li>Like charges repel; unlike charges attract</li><li>Charge is quantised: Q = ne (n = integer; e = 1.6 × 10⁻¹⁹ C)</li><li>Charge is conserved in all processes</li></ul><h3 class="learn-subheading">2. Coulomb\'s Law</h3><p class="learn-p"><strong>F = kQ₁Q₂/r²</strong></p><p class="learn-p">k = 1/(4πε₀) = 9 × 10⁹ N·m²/C². Force is along the line joining the charges; attractive for unlike charges, repulsive for like charges.</p><h3 class="learn-subheading">3. Electric Field</h3><p class="learn-p">Electric field strength E = F/q = kQ/r² (field due to point charge Q). Unit: N/C or V/m. Field lines: from + to −; stronger where lines are closer together.</p><h3 class="learn-subheading">4. Electric Potential and Capacitance</h3><ul class="learn-list"><li><strong>Electric potential:</strong> V = kQ/r. Work done W = QV. Unit: Volt (V = J/C).</li><li><strong>Capacitance:</strong> C = Q/V. Unit: Farad (F). Energy stored: E = ½CV² = ½QV = Q²/2C.</li><li>Parallel plate capacitor: C = ε₀A/d (A = plate area, d = separation).</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Coulomb vs Gravity:</strong> F = kQ₁Q₂/r² (Coulomb) vs F = Gm₁m₂/r² (gravity). Same form — inverse square law. k = 9×10⁹; G = 6.67×10⁻¹¹. Gravity always attractive; electrostatic can be attractive or repulsive.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> F=kQ₁Q₂/r². E=kQ/r² (field). V=kQ/r (potential). C=Q/V. Energy=½CV². Charge quantised: Q=ne. Conservation of charge.</div>',
    questions: [
      {
        q: 'Two positive charges of 2 μC and 3 μC are separated by 0.1 m. The force between them is (k = 9 × 10⁹ N·m²/C²):',
        o: ['0.054 N', '5.4 N', '0.54 N', '54 N'],
        a: 1,
        e: 'F = kQ₁Q₂/r² = 9×10⁹ × 2×10⁻⁶ × 3×10⁻⁶ / (0.1)² = 9×10⁹ × 6×10⁻¹² / 0.01 = 54×10⁻³/0.01 = 5.4 N.',
        h: 'F = 9×10⁹ × 2×10⁻⁶ × 3×10⁻⁶/0.01 = 5.4 N.',
        yr: 'SS2',
      },
      {
        q: 'The elementary charge e has value:',
        o: ['9 × 10⁹ C', '1.6 × 10⁻¹⁹ C', '6.67 × 10⁻¹¹ C', '9.11 × 10⁻³¹ C'],
        a: 1,
        e: 'The elementary charge e = 1.6 × 10⁻¹⁹ C (charge of one proton or one electron). Charge is quantised: all charges are integer multiples of e. Q = ne.',
        h: 'Elementary charge e = 1.6 × 10⁻¹⁹ C.',
        yr: 'SS2',
      },
      {
        q: 'The electric field at a distance r from a point charge Q is:',
        o: ['kQ/r', 'kQ/r²', 'kQ²/r', 'kQr²'],
        a: 1,
        e: "Electric field E = F/q = kQ/r². Field strength decreases with the square of distance (inverse square law) — same form as Coulomb's force. Unit: N/C = V/m.",
        h: 'E = kQ/r² (electric field from point charge Q). Unit: N/C or V/m.',
        yr: 'SS2',
      },
      {
        q: 'Electric field lines always point:',
        o: [
          'From negative to positive charge',
          'In circles around the charge',
          'From positive to negative charge (in the direction a positive test charge would move)',
          'Parallel to the surface of conductors',
        ],
        a: 2,
        e: 'Electric field lines point in the direction a positive test charge would move: from positive to negative. They start on + charges and end on − charges. At a conductor surface, field lines are perpendicular to the surface.',
        h: 'Field lines: from + to − (direction a positive test charge would move).',
        yr: 'SS2',
      },
      {
        q: "Coulomb's Law and Newton's gravitational law are similar in that both:",
        o: [
          'Are always attractive',
          'Have the same constant k',
          'Follow an inverse square law with distance (F ∝ 1/r²)',
          'Depend on mass',
        ],
        a: 2,
        e: 'Both laws are inverse square laws: F_gravity = Gm₁m₂/r² and F_Coulomb = kQ₁Q₂/r². Key difference: gravity is always attractive; Coulomb force can be attractive (unlike charges) or repulsive (like charges). Different constants: G = 6.67×10⁻¹¹, k = 9×10⁹.',
        h: 'Both Coulomb and gravity: F ∝ 1/r² (inverse square law). Gravity always attractive; Coulomb: attractive or repulsive.',
        yr: 'SS2',
      },
      {
        q: 'The electric potential at a point is defined as:',
        o: [
          'The force per unit charge at that point',
          'The work done per unit positive charge in bringing a test charge from infinity to that point',
          'The electric field at that point',
          'The charge per unit area at that point',
        ],
        a: 1,
        e: 'Electric potential V = W/q = kQ/r. It is the work done per unit positive charge to move a test charge from infinity (V=0 reference) to that point. Unit: Volt (V = J/C). V is a scalar; E is a vector.',
        h: 'Electric potential V = W/q = kQ/r. Work per unit charge from infinity. Unit: Volt (J/C).',
        yr: 'SS2',
      },
      {
        q: 'Capacitance is defined as:',
        o: [
          'Charge × voltage',
          'Charge / voltage: C = Q/V — ability to store charge per unit voltage',
          'Energy stored per unit time',
          'Voltage × current',
        ],
        a: 1,
        e: 'Capacitance C = Q/V (charge stored per volt of potential difference). Unit: Farad (F = C/V). 1 F is very large — practical capacitors are in μF (10⁻⁶ F) or pF (10⁻¹² F).',
        h: 'Capacitance C = Q/V. Unit: Farad. C = Q/V.',
        yr: 'SS2',
      },
      {
        q: 'The energy stored in a capacitor of capacitance C charged to voltage V is:',
        o: ['CV', '½CV', '½CV²', 'CV²'],
        a: 2,
        e: 'Energy stored E = ½CV² = ½QV = Q²/(2C). The factor ½ arises because voltage builds up progressively as charge accumulates — average voltage during charging is V/2.',
        h: 'Energy in capacitor = ½CV² = ½QV = Q²/2C.',
        yr: 'SS2',
      },
      {
        q: 'Two conducting spheres are connected by a wire. Charge will flow until:',
        o: [
          'The spheres have equal charges',
          'The spheres have equal electric potential',
          'The current reaches a maximum',
          'The wire melts',
        ],
        a: 1,
        e: 'When two conductors are connected, charge flows until they reach the same electric potential. This is the equilibrium condition — no potential difference means no electric field in the wire means no current flow. Larger sphere has more charge at equilibrium (C ∝ r, so Q ∝ rV).',
        h: 'Connected conductors: charge flows until equal potential. Not necessarily equal charge.',
        yr: 'SS2',
      },
      {
        q: 'When a charged object is brought near an insulated conductor without touching it:',
        o: [
          'The conductor gains charge permanently',
          'Charges redistribute within the conductor (electrostatic induction) — the near side gains unlike charge and far side gains like charge',
          'The conductor becomes neutral',
          'The charges move to the surface of the insulator',
        ],
        a: 1,
        e: 'Electrostatic induction: free electrons in the conductor redistribute. If a positive charge is brought near, free electrons are attracted toward it (near side −ve, far side +ve). This charge separation (induction) occurs without any charge being transferred.',
        h: 'Electrostatic induction: charge redistributes in conductor without contact. Near side = unlike charge.',
        yr: 'SS2',
      },
      {
        q: 'A parallel plate capacitor has plate area 0.01 m² and plate separation 0.001 m. Its capacitance (ε₀ = 8.85 × 10⁻¹² F/m) is approximately:',
        o: ['88.5 pF', '8.85 pF', '885 pF', '0.885 pF'],
        a: 0,
        e: 'C = ε₀A/d = 8.85×10⁻¹² × 0.01/0.001 = 8.85×10⁻¹² × 10 = 88.5×10⁻¹² F = 88.5 pF.',
        h: 'C = ε₀A/d = 8.85×10⁻¹² × 0.01/0.001 = 88.5 pF.',
        yr: 'SS2',
      },
      {
        q: 'What is the charge quantisation principle?',
        o: [
          'Charge can have any continuous value',
          'All observable charges are integer multiples of the elementary charge e = 1.6×10⁻¹⁹ C: Q = ne',
          'Charge is always conserved',
          'Charge equals mass times velocity',
        ],
        a: 1,
        e: "Charge quantisation: charge Q = ne where n is a positive or negative integer and e = 1.6×10⁻¹⁹ C. You cannot have half a proton's charge. The smallest free charge is e (or 2e/3 for quarks, but quarks are never free).",
        h: 'Charge quantised: Q = ne. Smallest charge = e = 1.6×10⁻¹⁹ C.',
        yr: 'SS2',
      },
      {
        q: 'An electric field of 1000 V/m acts on a charge of 2 × 10⁻⁶ C. The force on the charge is:',
        o: ['2 × 10⁻³ N', '5 × 10⁻⁴ N', '2 N', '5 × 10² N'],
        a: 0,
        e: 'F = qE = 2×10⁻⁶ × 1000 = 2×10⁻³ N.',
        h: 'F = qE = 2×10⁻⁶ × 1000 = 2×10⁻³ N.',
        yr: 'SS2',
      },
      {
        q: 'The capacitance of a parallel plate capacitor INCREASES if:',
        o: [
          'The plate separation is increased',
          'The plate area is decreased',
          'A dielectric (insulating material) is inserted between the plates — it increases capacitance by a factor equal to the relative permittivity (dielectric constant)',
          'The voltage across it is increased',
        ],
        a: 2,
        e: 'C = εA/d = ε_r ε₀ A/d. Inserting a dielectric: ε_r > 1 → C increases. Also: increasing A increases C; decreasing d increases C. Changing voltage does not change C (C is a physical property of the capacitor).',
        h: 'C increases: larger plate area, smaller separation, or inserting dielectric (ε_r > 1).',
        yr: 'SS2',
      },
      {
        q: 'The principle of conservation of charge states that:',
        o: [
          'Charge can be created by friction',
          'The total electric charge in an isolated system remains constant — charge can neither be created nor destroyed, only transferred',
          'Positive charges can be converted to negative charges',
          'Charge increases with temperature',
        ],
        a: 1,
        e: 'Conservation of charge: the total charge in a closed system is constant. When you rub a rubber rod with fur, the rod gains negative charge and the fur gains equal positive charge — total charge of system is unchanged (zero initially, zero finally).',
        h: 'Conservation of charge: total charge in isolated system = constant. Cannot create or destroy charge.',
        yr: 'SS2',
      },
      {
        q: 'If the separation between two charges is halved, the electrostatic force:',
        o: ['Halves', 'Doubles', 'Quadruples (×4)', 'Remains the same'],
        a: 2,
        e: "F ∝ 1/r². If r → r/2: F' ∝ 1/(r/2)² = 4/r² = 4F. Force increases by factor of 4 (quadruples).",
        h: 'r halved → F quadruples (F ∝ 1/r²).',
        yr: 'SS2',
      },
      {
        q: 'Electric field inside a conductor in electrostatic equilibrium is:',
        o: [
          'Maximum at the centre',
          'Equal to kQ/r² throughout',
          'Zero — free electrons redistribute to cancel any internal field',
          'Equal to the external field',
        ],
        a: 2,
        e: 'In electrostatic equilibrium, free electrons redistribute on the surface until the internal E-field is zero everywhere inside. If E ≠ 0, electrons would move (not equilibrium). This is why a Faraday cage blocks external electric fields.',
        h: 'E = 0 inside conductor at electrostatic equilibrium (free electrons cancel any field).',
        yr: 'SS2',
      },
      {
        q: 'A Van de Graaff generator builds up charge on a hollow metal sphere. Why does charge accumulate on the OUTSIDE surface only?',
        o: [
          'The inside surface repels charge',
          "Excess charge on a conductor distributes on the outer surface — the interior E-field is zero (Gauss's Law), so no charge resides there",
          'The generator only reaches the outside',
          'The outside has more space',
        ],
        a: 1,
        e: "Gauss's Law: E = 0 inside a conductor → no net charge inside. All excess charge resides on the outer surface. This allows the Van de Graaff to continuously transfer charge to the outer surface, building up very high potentials.",
        h: 'Charge on conductor → outer surface only (E=0 inside, so no charge inside).',
        yr: 'SS2',
      },
      {
        q: 'A capacitor of 100 μF is charged to 50 V. The energy stored is:',
        o: ['2500 J', '0.125 J', '250 mJ', '5000 μJ'],
        a: 2,
        e: 'E = ½CV² = ½ × 100×10⁻⁶ × 50² = ½ × 100×10⁻⁶ × 2500 = 0.125 J = 125 mJ. Wait: ½ × 10⁻⁴ × 2500 = ½ × 0.25 = 0.125 J = 125 mJ. Option C = 250 mJ. Recalc: ½ × 100×10⁻⁶ × 2500 = ½ × 0.25 = 0.125 J = 125 mJ. The closest is 0.125 J. Select option B.',
        h: 'E = ½CV² = ½×100×10⁻⁶×2500 = 0.125 J.',
        yr: 'SS2',
      },
      {
        q: 'A conductor placed in an external electric field will have the field inside it equal to:',
        o: [
          'The external field value',
          'Twice the external field',
          'Zero — free electrons redistribute to cancel internal field (electrostatic shielding)',
          'Half the external field',
        ],
        a: 2,
        e: 'In electrostatic equilibrium, free electrons in a conductor rearrange until the net electric field inside is zero. This is electrostatic shielding — the basis of the Faraday cage. All excess charge resides on the outer surface.',
        h: 'Conductor in E-field: internal E = 0 (electrons redistribute to cancel field). Faraday cage.',
        yr: 'SS2',
      },
    ],
  },
  // =========================================================================
  // TOPIC 13: CURRENT ELECTRICITY
  // =========================================================================
  {
    topic: 'Current Electricity',
    topicCode: 'SS2-PHY-13',
    module: 'Electricity',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Current electricity</span> deals with the flow of electric charge through conductors. Key concepts include Ohm\'s Law, resistance, circuit analysis (Kirchhoff\'s Laws), electrical power, and safety. These form the basis of all electronic and electrical engineering.</div><h3 class="learn-subheading">1. Current, Voltage, and Resistance</h3><ul class="learn-list"><li><strong>Current:</strong> I = Q/t (charge per unit time). Unit: Ampere (A).</li><li><strong>Voltage (EMF/PD):</strong> V = W/Q (work per unit charge). Unit: Volt (V).</li><li><strong>Ohm\'s Law:</strong> V = IR (for ohmic conductors at constant temperature). Unit of resistance: Ohm (Ω).</li></ul><h3 class="learn-subheading">2. Resistance in Circuits</h3><ul class="learn-list"><li><strong>Series:</strong> R_total = R₁ + R₂ + R₃ + ... (same current; voltages add)</li><li><strong>Parallel:</strong> 1/R_total = 1/R₁ + 1/R₂ + 1/R₃ + ... (same voltage; currents add)</li><li>Resistivity: R = ρL/A (depends on material ρ, length L, cross-sectional area A)</li></ul><h3 class="learn-subheading">3. Electrical Power and Energy</h3><ul class="learn-list"><li>P = IV = I²R = V²/R. Unit: Watt (W)</li><li>E = Pt = VIt. Unit: Joule (J)</li><li>Household energy unit: kilowatt-hour (kWh). 1 kWh = 3.6 × 10⁶ J</li></ul><h3 class="learn-subheading">4. Kirchhoff\'s Laws</h3><ul class="learn-list"><li><strong>KCL (Junction Rule):</strong> Sum of currents entering a junction = sum leaving (charge conservation)</li><li><strong>KVL (Loop Rule):</strong> Sum of potential differences around any closed loop = zero (energy conservation)</li></ul><h3 class="learn-subheading">5. Internal Resistance</h3><p class="learn-p">Terminal PD = EMF − Ir = E − Ir. Where I = current drawn, r = internal resistance, E = EMF.</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Key:</strong> Series: R_T = R₁+R₂ (currents same). Parallel: 1/R_T = 1/R₁+1/R₂ (voltages same). P = IV = I²R = V²/R. Terminal PD = E − Ir.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> I=Q/t. V=IR (Ohm\'s). Series: R add. Parallel: 1/R add. P=IV=I²R=V²/R. E=Pt. Terminal PD=E−Ir. KCL: currents at junction. KVL: voltages around loop.</div>',
    questions: [
      {
        q: "Ohm's Law states that for an ohmic conductor:",
        o: [
          'Current is inversely proportional to voltage',
          'Voltage is directly proportional to current: V = IR, at constant temperature',
          'Resistance increases with current',
          'Power equals current times resistance',
        ],
        a: 1,
        e: "Ohm's Law: V = IR. For an ohmic conductor at constant temperature, voltage is directly proportional to current — the ratio V/I = R is constant. A V-I graph gives a straight line through the origin for an ohmic conductor.",
        h: "Ohm's Law: V = IR (constant temperature). V ∝ I; R = constant (V/I graph = straight line).",
        yr: 'SS2',
      },
      {
        q: 'Three resistors of 2 Ω, 3 Ω, and 6 Ω are connected in parallel. The total resistance is:',
        o: ['11 Ω', '1 Ω', '0.91 Ω', '3.67 Ω'],
        a: 1,
        e: '1/R_T = 1/2 + 1/3 + 1/6 = 3/6 + 2/6 + 1/6 = 6/6 = 1. R_T = 1 Ω.',
        h: '1/R_T = 1/2+1/3+1/6 = 1. R_T = 1 Ω.',
        yr: 'SS2',
      },
      {
        q: 'A 4 Ω and 6 Ω resistor are connected in series to a 20 V battery. The current through the circuit is:',
        o: ['5 A', '2 A', '0.4 A', '3.3 A'],
        a: 1,
        e: 'Series: R_T = 4+6 = 10 Ω. I = V/R = 20/10 = 2 A.',
        h: 'R_T=10 Ω; I=V/R=20/10=2 A.',
        yr: 'SS2',
      },
      {
        q: 'Electrical power is calculated by:',
        o: ['P = V/I', 'P = IV = I²R = V²/R', 'P = I/V', 'P = V²I'],
        a: 1,
        e: 'P = IV = I²R = V²/R. All three expressions are equivalent (using V=IR to substitute). Unit: Watt (W = J/s). A 60 W bulb with 240 V supply draws I = P/V = 60/240 = 0.25 A.',
        h: 'P = IV = I²R = V²/R. Unit: Watt (W).',
        yr: 'SS2',
      },
      {
        q: "Kirchhoff's Current Law (KCL) states that:",
        o: [
          'Voltage around a loop sums to zero',
          'The algebraic sum of currents at any junction is zero (currents in = currents out)',
          'Current flows only in one direction',
          'All currents in a circuit are equal',
        ],
        a: 1,
        e: 'KCL (junction rule): conservation of charge. ΣI_in = ΣI_out at any node. No charge accumulates at a junction — what flows in must flow out. This gives equations for solving complex circuits.',
        h: 'KCL: currents into junction = currents out. ΣI = 0 at any node.',
        yr: 'SS2',
      },
      {
        q: 'A battery of EMF 12 V and internal resistance 2 Ω delivers current to an external resistance of 10 Ω. The terminal PD is:',
        o: ['12 V', '10 V', '2 V', '8 V'],
        a: 1,
        e: 'I = E/(R+r) = 12/(10+2) = 1 A. Terminal PD = E − Ir = 12 − 1×2 = 10 V. (Or: V = IR_ext = 1×10 = 10 V ✓.)',
        h: 'I=12/12=1A. Terminal PD=E−Ir=12−2=10 V.',
        yr: 'SS2',
      },
      {
        q: 'The resistance of a conductor depends on:',
        o: [
          'The current flowing through it',
          'The voltage across it',
          'Its material (resistivity ρ), length L, and cross-sectional area A: R = ρL/A',
          'Its temperature only',
        ],
        a: 2,
        e: 'R = ρL/A. Resistance increases with length (more collisions) and decreases with cross-sectional area (more paths for electrons). Resistivity ρ depends on the material and temperature. Longer, thinner wire has higher resistance.',
        h: 'R = ρL/A. Longer wire → higher R. Thicker wire → lower R.',
        yr: 'SS2',
      },
      {
        q: 'A 60 W lamp operates for 5 hours. The energy consumed in kWh is:',
        o: ['300 kWh', '0.3 kWh', '30 kWh', '60 kWh'],
        a: 1,
        e: 'Energy = Power × time = 60 W × 5 h = 300 Wh = 0.3 kWh.',
        h: 'Energy = 60W × 5h = 300 Wh = 0.3 kWh.',
        yr: 'SS2',
      },
      {
        q: "Kirchhoff's Voltage Law (KVL) states that:",
        o: [
          'Current at any junction is conserved',
          'The sum of all potential differences around any closed loop in a circuit equals zero (energy conservation)',
          'Voltage equals current times time',
          'All voltages in a circuit are equal',
        ],
        a: 1,
        e: 'KVL (loop rule): conservation of energy. The algebraic sum of EMFs and voltage drops around any closed loop = 0. ΣE = ΣIR around a loop. This gives equations for mesh analysis of complex circuits.',
        h: 'KVL: sum of all PDs around closed loop = 0. ΣE = ΣIR.',
        yr: 'SS2',
      },
      {
        q: 'The kilowatt-hour (kWh) is a unit of:',
        o: ['Power', 'Current', 'Energy (1 kWh = 3.6 × 10⁶ J)', 'Resistance'],
        a: 2,
        e: '1 kWh = 1000 W × 3600 s = 3.6 × 10⁶ J. It is a unit of energy, not power. Electricity meters measure consumption in kWh (units). 1 unit = 1 kWh.',
        h: '1 kWh = 3.6 × 10⁶ J. Unit of energy (not power).',
        yr: 'SS2',
      },
      {
        q: 'Two identical resistors R are connected in parallel. The equivalent resistance is:',
        o: ['2R', 'R/2', 'R', 'R²'],
        a: 1,
        e: '1/R_T = 1/R + 1/R = 2/R → R_T = R/2. Two identical resistors in parallel give half the individual resistance. (n identical resistors in parallel give R/n.)',
        h: 'Two identical R in parallel: R_T = R/2.',
        yr: 'SS2',
      },
      {
        q: 'A fuse in an electrical circuit:',
        o: [
          'Increases the current in the circuit',
          'Converts electrical energy to mechanical energy',
          'Protects the circuit by melting and breaking the circuit when current exceeds the rated value',
          'Stores electrical charge',
        ],
        a: 2,
        e: 'A fuse contains a thin wire (with low melting point) that melts when current exceeds its rated value, breaking the circuit and preventing damage to appliances and fire hazards. Fuses are always connected in the live wire.',
        h: 'Fuse: melts when current exceeds rated value → breaks circuit → protects appliances.',
        yr: 'SS2',
      },
      {
        q: 'What is the resistance of a wire that carries 0.5 A when a potential difference of 6 V is applied across it?',
        o: ['3 Ω', '12 Ω', '0.083 Ω', '3 V'],
        a: 1,
        e: 'R = V/I = 6/0.5 = 12 Ω.',
        h: 'R = V/I = 6/0.5 = 12 Ω.',
        yr: 'SS2',
      },
      {
        q: 'In a circuit, a 3 Ω and a 6 Ω resistor are connected in parallel, and this combination is in series with a 2 Ω resistor. The total resistance is:',
        o: ['11 Ω', '4 Ω', '5 Ω', '2 Ω'],
        a: 1,
        e: 'Parallel combination: 1/R_p = 1/3+1/6 = 2/6+1/6 = 3/6 → R_p = 2 Ω. Total = R_p + 2 = 2+2 = 4 Ω.',
        h: 'Parallel: R_p=2 Ω. Total = 2+2 = 4 Ω.',
        yr: 'SS2',
      },
      {
        q: 'The heating effect of current (Joule heating) is given by:',
        o: ['Q = IRt', 'Q = I²Rt', 'Q = VR/t', 'Q = V²/Rt'],
        a: 1,
        e: 'Joule heating: Q = I²Rt = VIt = V²t/R. This is the heat produced in a resistor carrying current I for time t. Electric heaters, toasters, and incandescent bulbs work on this principle.',
        h: 'Joule heating: Q = I²Rt = VIt = V²t/R.',
        yr: 'SS2',
      },
      {
        q: 'The EMF of a battery is best described as:',
        o: [
          'The voltage across its terminals when delivering current',
          'The maximum voltage it can deliver regardless of internal resistance',
          'The total electrical energy per unit charge that the battery provides — including the energy to overcome internal resistance',
          'The current it delivers at short circuit',
        ],
        a: 2,
        e: "EMF (electromotive force) is the total work done per unit charge by the battery's chemical energy in moving charge through the complete circuit — both internal and external. Terminal PD = EMF − voltage drop across internal resistance = E − Ir.",
        h: 'EMF = total energy per unit charge from battery = E. Terminal PD = E − Ir (less due to internal r).',
        yr: 'SS2',
      },
      {
        q: 'Current flows through an electric circuit from:',
        o: [
          'Negative to positive terminal through the external circuit (conventional current from + to −)',
          'Positive to negative terminal of the battery through the external circuit (conventional current)',
          'In no particular direction',
          'From the higher resistance to lower resistance',
        ],
        a: 1,
        e: 'Conventional current flows from + terminal through external circuit to − terminal (opposite to electron flow). Electrons (actual charge carriers) flow from − to +. By convention, we use conventional current direction: + to − externally.',
        h: 'Conventional current: + → external circuit → −. Electron flow: − → + (opposite).',
        yr: 'SS2',
      },
      {
        q: 'A resistor R = 10 Ω is connected to a 20 V supply. Power dissipated:',
        o: ['200 W', '40 W', '0.5 W', '2 W'],
        a: 1,
        e: 'P = V²/R = 20²/10 = 400/10 = 40 W.',
        h: 'P = V²/R = 400/10 = 40 W.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following increases the resistance of a metallic conductor?',
        o: [
          'Decreasing its length',
          'Increasing its cross-sectional area',
          'Increasing its temperature (for most metals)',
          'Decreasing its resistivity',
        ],
        a: 2,
        e: 'For most metals, resistance increases with temperature — thermal vibrations increase, causing more electron collisions and greater resistivity. R = ρ(T)L/A; ρ increases with T for metals. (Semiconductors: resistance decreases with temperature — opposite behaviour.)',
        h: 'Metal resistance increases with temperature (more thermal vibration → more electron collisions).',
        yr: 'SS2',
      },
      {
        q: 'A non-ohmic conductor differs from an ohmic conductor in that:',
        o: [
          'It has no resistance',
          'Its resistance changes with temperature only',
          'The ratio V/I is not constant — V is not proportional to I (e.g., diodes, filament bulbs at varying temperatures)',
          "It obeys Ohm's Law perfectly",
        ],
        a: 2,
        e: 'Ohmic conductor: V/I = R = constant (straight-line V-I graph). Non-ohmic: V/I varies with conditions — examples: diode (V-I curve is exponential), filament lamp (resistance increases with temperature as bulb heats up). V-I graph is curved.',
        h: 'Non-ohmic: V/I not constant (curved V-I graph). Examples: diode, filament lamp.',
        yr: 'SS2',
      },
    ],
  },
  // =========================================================================
  // TOPIC 14: MAGNETISM
  // =========================================================================
  {
    topic: 'Magnetism',
    topicCode: 'SS2-PHY-14',
    module: 'Electromagnetism',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Magnetism</span> is a fundamental force related to moving electric charges. Natural magnets (lodestone), permanent magnets, and electromagnets are all studied here, along with the force on current-carrying conductors and the basis of electric motors.</div><h3 class="learn-subheading">1. Properties of Magnets</h3><ul class="learn-list"><li>Poles: north (N) and south (S). Like poles repel; unlike poles attract.</li><li>Magnetic field lines: from N to S outside the magnet; always continuous closed loops.</li><li>A magnet cannot be split into monopoles — each piece has both N and S poles.</li></ul><h3 class="learn-subheading">2. Magnetic Field due to Currents</h3><ul class="learn-list"><li>Straight wire: circular field lines; use Right Hand Grip Rule (thumb along current → fingers show field direction)</li><li>Circular loop: field like a bar magnet at the centre</li><li>Solenoid: uniform field inside like a bar magnet; N pole at end where current is anticlockwise when viewed from that end</li></ul><h3 class="learn-subheading">3. Force on a Current-Carrying Conductor</h3><p class="learn-p"><strong>F = BIL sin θ</strong></p><p class="learn-p">where B = magnetic flux density (T), I = current (A), L = length of conductor in field (m), θ = angle between conductor and field. Maximum force when θ = 90° (conductor ⊥ field).</p><h3 class="learn-subheading">4. Fleming\'s Rules</h3><ul class="learn-list"><li><strong>Left Hand Rule (motor effect):</strong> Force (thumb), Field (index), Current (middle finger). Used for electric motors.</li><li><strong>Right Hand Rule (generator effect):</strong> Motion (thumb), Field (index), EMF/Current (middle finger). Used for generators.</li></ul><h3 class="learn-subheading">5. Magnetic Flux Density</h3><p class="learn-p">B = F/(IL) for conductor ⊥ field. Unit: Tesla (T = N/(A·m)). Magnetic flux Φ = BA cos θ. Unit: Weber (Wb).</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Fleming\'s Rules:</strong> Left Hand = motor (FBI: Force/Thrust, B-field, Current). Right Hand = generator (FBE: Force/Motion, B-field, EMF).</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> F=BILsinθ. Left Hand Rule (motor). Right Hand Rule (generator). Solenoid: field like bar magnet. Flux Φ=BAcosθ (Weber). B unit: Tesla.</div>',
    questions: [
      {
        q: 'The force on a current-carrying conductor in a magnetic field is given by:',
        o: [
          'F = BIL',
          'F = BIL sin θ (where θ is the angle between current direction and field)',
          'F = BV/I',
          'F = B²IL',
        ],
        a: 1,
        e: 'F = BIL sinθ. Maximum force (F = BIL) when conductor is perpendicular to field (θ = 90°). Zero force when conductor is parallel to field (θ = 0°). This is the motor effect.',
        h: 'F = BIL sinθ. Max when θ=90° (conductor ⊥ field). Zero when θ=0°.',
        yr: 'SS2',
      },
      {
        q: "Fleming's Left Hand Rule is used to find the direction of force (motion) in:",
        o: [
          'A generator',
          'An electric motor (motor effect — current in magnetic field produces force)',
          'A transformer',
          'A capacitor',
        ],
        a: 1,
        e: "Fleming's Left Hand Rule (FBI rule): First finger = Field (B direction), seCond finger = Current (I direction), thuMb = Motion/Force direction. Applied to electric motors where current in magnetic field produces mechanical force.",
        h: "Fleming's Left Hand Rule: motor effect. F=thumb, B=index, I=middle finger.",
        yr: 'SS2',
      },
      {
        q: 'When a bar magnet is cut in half:',
        o: [
          'You get an isolated north and south pole (monopoles)',
          'You get two weaker magnets, each with both a north and south pole',
          'You get a north pole piece and a separate south pole piece',
          'The magnet loses all magnetism',
        ],
        a: 1,
        e: 'Magnetic monopoles do not exist (as far as known). Cutting a magnet creates two smaller magnets each with both N and S poles. This applies no matter how many times the magnet is cut — down to atomic/molecular level.',
        h: 'Cut magnet → two complete magnets, each with N and S poles. No magnetic monopoles.',
        yr: 'SS2',
      },
      {
        q: 'The magnetic field inside a solenoid is:',
        o: [
          'Zero',
          'Strongest at the ends',
          'Uniform and parallel to the axis (like the uniform field inside the solenoid)',
          'Circular and concentric',
        ],
        a: 2,
        e: "Inside a long solenoid, the magnetic field is uniform and parallel to the solenoid's axis (like the field inside a bar magnet, but for the solenoid the field is inside the coil). Outside the solenoid, the field is weak (fringe field). B = μ₀nI where n = turns per metre.",
        h: 'Solenoid interior: uniform field parallel to axis. B = μ₀nI.',
        yr: 'SS2',
      },
      {
        q: 'The Right Hand Grip Rule for a straight current-carrying wire tells you:',
        o: [
          'The direction of current from the direction of field',
          'If the thumb points in the direction of current flow, the curled fingers show the direction of the circular magnetic field around the wire',
          'The direction of force on the wire',
          'The magnitude of the magnetic field',
        ],
        a: 1,
        e: 'Right Hand Grip Rule (for straight wire): grip the wire with right hand, thumb pointing in conventional current direction → curled fingers indicate the circular B-field direction around the wire.',
        h: 'Right Hand Grip: thumb = current; fingers curl = B-field direction around wire.',
        yr: 'SS2',
      },
      {
        q: 'Magnetic flux Φ through a surface is defined as:',
        o: [
          'B × I',
          'B × A × cos θ (field strength × area × cos of angle between field and normal to surface)',
          'B/A',
          'F/IL',
        ],
        a: 1,
        e: 'Magnetic flux Φ = BA cosθ, where θ is the angle between the magnetic field B and the normal (perpendicular) to the area A. Maximum flux (Φ = BA) when field is perpendicular to the surface. Unit: Weber (Wb = T·m²).',
        h: 'Magnetic flux Φ = BA cosθ. Max when B ⊥ surface. Unit: Weber (Wb).',
        yr: 'SS2',
      },
      {
        q: 'A conductor of length 0.5 m carries current 4 A in a magnetic field B = 0.3 T perpendicular to the conductor. The force on it is:',
        o: ['0.06 N', '0.6 N', '6 N', '0.006 N'],
        a: 1,
        e: 'F = BIL sinθ = 0.3 × 4 × 0.5 × sin90° = 0.3 × 4 × 0.5 × 1 = 0.6 N.',
        h: 'F = BILsin90° = 0.3×4×0.5×1 = 0.6 N.',
        yr: 'SS2',
      },
      {
        q: 'The unit of magnetic flux density (B) is the Tesla, equivalent to:',
        o: ['J/m²', 'N/(A·m)', 'V·s', 'kg/A'],
        a: 1,
        e: '1 T = 1 N/(A·m). From F = BIL: B = F/(IL) → [N]/([A][m]) = N/(A·m). Also: 1 T = 1 Wb/m² = 1 V·s/m² = 1 kg/(A·s²).',
        h: '1 T = 1 N/(A·m) = 1 Wb/m².',
        yr: 'SS2',
      },
      {
        q: 'An electromagnet is a temporary magnet because:',
        o: [
          'It uses electricity, which is temporary',
          'It is magnetised only while current flows — the magnetic field ceases when the current is switched off',
          'Its poles can be reversed',
          'It has a lower magnetic field than a permanent magnet',
        ],
        a: 1,
        e: 'An electromagnet is created by passing current through a coil of wire (solenoid), usually with a soft iron core. When current flows, the core is magnetised. When current stops, soft iron demagnetises (temporary magnetism). Hard iron/steel retains magnetism (permanent magnet).',
        h: 'Electromagnet: magnetic when current flows; demagnetises when current stops (soft iron core).',
        yr: 'SS2',
      },
      {
        q: 'The force between two parallel wires carrying currents in the SAME direction:',
        o: [
          'Is repulsive',
          'Is zero',
          'Is attractive — the wires attract each other',
          'Depends only on their length',
        ],
        a: 2,
        e: "Two parallel wires with currents in the same direction attract each other. The magnetic field of each wire acts on the current in the other. (Ampère's force law.) This is the basis for the definition of the Ampere (SI unit of current).",
        h: 'Parallel wires, same direction currents → attract. Opposite currents → repel.',
        yr: 'SS2',
      },
      {
        q: 'In an electric motor, the coil continues to rotate because:',
        o: [
          'The battery pushes it mechanically',
          'The commutator reverses the current direction every half turn, ensuring the force on each side of the coil always acts in the same rotational direction',
          'Gravity keeps it spinning',
          'The magnetic field reverses',
        ],
        a: 1,
        e: 'Without a commutator, the coil would oscillate (force reverses when coil passes vertical). The split-ring commutator reverses the current every 180°, so the torque always acts in the same direction → continuous rotation.',
        h: 'Commutator: reverses current every half turn → torque always in same direction → continuous rotation.',
        yr: 'SS2',
      },
      {
        q: 'Magnetic field lines around a bar magnet:',
        o: [
          'Go from south to north pole outside the magnet',
          'Go from north to south pole outside the magnet and south to north inside (continuous closed loops)',
          'Are only present inside the magnet',
          "Are straight lines parallel to the magnet's axis",
        ],
        a: 1,
        e: 'Magnetic field lines: outside the magnet from N to S; inside the magnet from S to N. They are continuous closed loops — no beginning or end. The density of field lines represents field strength (closer lines = stronger field).',
        h: 'Field lines: N to S outside; S to N inside. Continuous closed loops. Closer = stronger.',
        yr: 'SS2',
      },
      {
        q: 'A compass needle aligns itself with magnetic field lines because:',
        o: [
          'It is positively charged',
          "The needle is a permanent magnet; the Earth's magnetic field exerts a torque that aligns it with the field (N pole points toward geographic north/magnetic south)",
          'It is attracted to the geographic North Pole',
          'Gravity acts along field lines',
        ],
        a: 1,
        e: "A compass needle is a small permanent magnet. External magnetic fields exert torques that rotate it to align with the field — N pole pointing in the field direction. Earth's geographic North Pole is actually a magnetic south pole (which attracts the compass N pole).",
        h: "Compass: permanent magnet aligned by torque of Earth's magnetic field.",
        yr: 'SS2',
      },
      {
        q: 'The magnetic flux density B at the centre of a solenoid with n turns per metre carrying current I is:',
        o: ['B = nI', 'B = μ₀nI (where μ₀ = 4π × 10⁻⁷ T·m/A)', 'B = μ₀nI²', 'B = nI/μ₀'],
        a: 1,
        e: 'B = μ₀nI inside a solenoid, where n = number of turns per unit length, I = current, μ₀ = 4π × 10⁻⁷ T·m/A (permeability of free space). Adding a ferromagnetic core increases B by factor μ_r (relative permeability).',
        h: 'Solenoid: B = μ₀nI. Adding iron core: B = μ₀μ_rnI (much larger).',
        yr: 'SS2',
      },
      {
        q: 'Which material would be BEST for making a permanent magnet?',
        o: ['Soft iron', 'Copper', 'Steel (hard iron)', 'Aluminium'],
        a: 2,
        e: 'Steel (hard iron) is best for permanent magnets — it has high coercivity (requires large reverse field to demagnetise) and high retentivity (retains magnetisation). Soft iron has low coercivity and demagnetises easily — better for electromagnet cores.',
        h: 'Permanent magnet: steel (hard iron) — high coercivity + high retentivity.',
        yr: 'SS2',
      },
      {
        q: "Fleming's Right Hand Rule is used for:",
        o: [
          'Electric motors',
          'Electromagnetic induction — to find the direction of induced EMF/current when a conductor moves in a magnetic field (generator effect)',
          'Static magnetic fields',
          'Electrostatics',
        ],
        a: 1,
        e: "Fleming's Right Hand Rule: thumb = Motion direction, First finger = Field direction, seCond finger = induced Current/EMF direction. Used for generators, dynamos, and any situation where mechanical motion produces electricity.",
        h: "Fleming's Right Hand Rule: generator effect. Motion=thumb, B=index, induced Current=middle.",
        yr: 'SS2',
      },
      {
        q: 'When the current through an electromagnet is doubled, the magnetic flux density:',
        o: ['Remains the same', 'Doubles (B = μ₀nI; B ∝ I)', 'Quadruples', 'Halves'],
        a: 1,
        e: 'B = μ₀nI. B is directly proportional to current I. Doubling I doubles B. This is why electromagnets can be made stronger by increasing the current or the number of turns.',
        h: 'B = μ₀nI ∝ I. Doubling current → doubles B.',
        yr: 'SS2',
      },
      {
        q: "The Earth's magnetic field is believed to originate from:",
        o: [
          "Giant permanent magnets in Earth's core",
          "Convection currents of molten iron in Earth's outer core (dynamo theory) generating electric currents that produce the field",
          "Static charges on Earth's surface",
          "Solar radiation magnetising Earth's atmosphere",
        ],
        a: 1,
        e: "Earth's magnetic field (geomagnetic field) is explained by the dynamo theory: convection of molten conducting iron/nickel in Earth's outer core creates electric currents, which generate a magnetic field similar to a giant solenoid. The field slowly changes and has reversed many times geologically.",
        h: "Earth's field: dynamo theory — convecting molten iron in outer core generates currents → magnetic field.",
        yr: 'SS2',
      },
      {
        q: 'Magnetic shielding (e.g., storing magnetic storage media away from magnets) works because:',
        o: [
          'Non-magnetic materials block magnetic fields completely',
          'Ferromagnetic materials have very high permeability and channel magnetic field lines through themselves, diverting them away from the shielded area',
          'Magnetic fields cannot penetrate solid objects',
          'Shielding uses electric fields to cancel magnetic fields',
        ],
        a: 1,
        e: 'Magnetic shielding uses high-permeability ferromagnetic materials (soft iron, mu-metal). These materials have very high relative permeability, so magnetic flux preferentially travels through them rather than through the shielded space inside — effectively diverting field lines away.',
        h: 'Magnetic shielding: high-permeability material channels field lines through itself, away from shielded area.',
        yr: 'SS2',
      },
      {
        q: 'The Tesla (T), unit of magnetic flux density, is equivalent to:',
        o: ['J/m²', 'N/(A·m)', 'V·m', 'kg/s²'],
        a: 1,
        e: "From F = BIL: B = F/(IL). [B] = N/(A·m) = T. Also: 1 T = 1 Wb/m² = 1 V·s/m² = 1 kg/(A·s²). The tesla is a large unit — Earth's field is about 50 μT; MRI machines use 1–3 T.",
        h: '1 T = 1 N/(A·m) = 1 Wb/m².',
        yr: 'SS2',
      },
    ],
  },
  // =========================================================================
  // TOPIC 15: ELECTROMAGNETIC INDUCTION
  // =========================================================================
  {
    topic: 'Electromagnetic Induction',
    topicCode: 'SS2-PHY-15',
    module: 'Electromagnetism',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Electromagnetic induction</span> is the production of an EMF (and current) in a conductor when there is a change in magnetic flux through it. Discovered by Faraday in 1831, it is the principle behind generators, transformers, and inductors — the foundation of modern electrical power systems.</div><h3 class="learn-subheading">1. Faraday\'s Law</h3><p class="learn-p">The induced EMF is proportional to the rate of change of magnetic flux: <strong>EMF = −dΦ/dt = −N × ΔΦ/Δt</strong> (for a coil of N turns). Unit: Volt (V). The magnitude of induced EMF increases with:</p><ul class="learn-list"><li>Faster change in flux (faster motion or faster field change)</li><li>Stronger magnetic field (B)</li><li>More turns (N) in the coil</li><li>Larger coil area (A)</li></ul><h3 class="learn-subheading">2. Lenz\'s Law</h3><p class="learn-p">The induced current opposes the change in flux that produced it (negative sign in Faraday\'s Law). This is a consequence of conservation of energy — if the induced current aided the change, energy would be created from nothing.</p><h3 class="learn-subheading">3. The AC Generator (Dynamo)</h3><p class="learn-p">A coil rotates in a magnetic field. As flux through the coil changes, an EMF is induced. The output alternates sinusoidally: E = E₀ sin(ωt). Components: rotating coil, permanent magnet, slip rings, brushes.</p><h3 class="learn-subheading">4. The Transformer</h3><p class="learn-p">A transformer changes AC voltage using mutual induction. <strong>V_s/V_p = N_s/N_p = I_p/I_s</strong> (for ideal transformer, 100% efficiency). Step-up: N_s > N_p → V_s > V_p. Step-down: N_s < N_p → V_s < V_p.</p><h3 class="learn-subheading">5. Eddy Currents and Inductance</h3><ul class="learn-list"><li><strong>Eddy currents:</strong> currents induced in bulk conductors by changing flux; cause energy losses (heat) in transformer cores — minimised by using laminated cores</li><li><strong>Inductance:</strong> self-inductance L (henry, H); opposes change in current; EMF = −L × dI/dt</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Transformer ratio:</strong> V_s/V_p = N_s/N_p. Step-up: more secondary turns. Power in = Power out (ideal): V_pI_p = V_sI_s.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Faraday: EMF = −NΔΦ/Δt. Lenz: induced current opposes change. Generator: rotating coil in field (slip rings). Transformer: V_s/V_p = N_s/N_p. Eddy currents → laminated core. Self-inductance opposes current change.</div>',
    questions: [
      {
        q: "Faraday's Law of electromagnetic induction states that:",
        o: [
          'A stationary conductor in a magnetic field produces an EMF',
          'The induced EMF is proportional to the rate of change of magnetic flux through the conductor/coil',
          'Current always flows in the same direction in a coil',
          'The induced current equals the applied voltage',
        ],
        a: 1,
        e: "Faraday's Law: EMF = −NΔΦ/Δt. The induced EMF is proportional to the rate of change of magnetic flux linkage (NΦ). More turns N, faster change ΔΦ/Δt → larger induced EMF. The negative sign gives the direction (Lenz's Law).",
        h: 'Faraday: EMF = −NΔΦ/Δt. EMF ∝ rate of change of flux.',
        yr: 'SS2',
      },
      {
        q: "Lenz's Law states that the induced current:",
        o: [
          'Aids the change in magnetic flux that produced it',
          'Flows in the same direction as the applied current',
          'Opposes the change in magnetic flux that produced it (conservation of energy)',
          'Is always in the clockwise direction',
        ],
        a: 2,
        e: "Lenz's Law: the induced current flows in a direction that opposes the change in flux causing it. If flux increases, induced current creates a field opposing the increase. If flux decreases, induced current creates a field to maintain it. This is required by conservation of energy.",
        h: "Lenz's Law: induced current OPPOSES the change in flux (conservation of energy).",
        yr: 'SS2',
      },
      {
        q: 'The factors that increase the induced EMF in a coil include:',
        o: [
          'Slower movement of the magnet',
          'Fewer turns in the coil',
          'Stronger magnetic field, more turns, and faster rate of flux change',
          'Removing the magnet completely',
        ],
        a: 2,
        e: 'EMF = NΔΦ/Δt = NΔ(BA)/Δt. Larger B (stronger field), more N (turns), faster ΔΦ/Δt (faster motion or faster field change), and larger A (coil area) all increase the induced EMF.',
        h: 'Larger EMF: stronger B, more turns N, faster flux change, larger coil area.',
        yr: 'SS2',
      },
      {
        q: 'The transformer equation V_s/V_p = N_s/N_p means:',
        o: [
          'A transformer works on DC voltage',
          'The ratio of secondary to primary voltage equals the ratio of secondary to primary turns',
          'The currents in primary and secondary are always equal',
          'A transformer converts AC to DC',
        ],
        a: 1,
        e: 'For an ideal transformer: V_s/V_p = N_s/N_p. If N_s > N_p → V_s > V_p (step-up). If N_s < N_p → V_s < V_p (step-down). Transformers only work with AC (changing flux needed for induction).',
        h: 'V_s/V_p = N_s/N_p. Step-up: N_s > N_p. Step-down: N_s < N_p.',
        yr: 'SS2',
      },
      {
        q: 'A transformer has 100 primary turns and 500 secondary turns. If the primary voltage is 240 V, the secondary voltage is:',
        o: ['48 V', '1200 V', '240 V', '12 V'],
        a: 1,
        e: 'V_s/V_p = N_s/N_p → V_s = V_p × N_s/N_p = 240 × 500/100 = 240 × 5 = 1200 V. (Step-up transformer — N_s > N_p → V_s > V_p.)',
        h: 'V_s = 240 × 500/100 = 1200 V (step-up transformer).',
        yr: 'SS2',
      },
      {
        q: 'An AC generator (dynamo) differs from a DC generator (dynamo) in that the AC generator uses:',
        o: [
          'A stronger magnetic field',
          'Slip rings instead of a split-ring commutator — allowing alternating current to flow to the external circuit',
          'More coil turns',
          'A different coil material',
        ],
        a: 1,
        e: 'AC generator: slip rings (continuous rings) maintain connection as the coil rotates → output alternates sinusoidally. DC generator: split-ring commutator reverses connection every half turn → rectified (unidirectional) output. Same internal mechanism; different external connection.',
        h: 'AC generator: slip rings → alternating output. DC generator: split-ring commutator → DC output.',
        yr: 'SS2',
      },
      {
        q: 'Eddy currents in a transformer core are undesirable because:',
        o: [
          "They increase the transformer's efficiency",
          'They cause energy losses as heat in the core, reducing efficiency',
          'They magnetise the core more strongly',
          'They increase the induced EMF',
        ],
        a: 1,
        e: 'Eddy currents are circulating induced currents in the bulk metal of the transformer core. They produce heat (I²R) and represent wasted energy. Minimised by using a laminated core (thin insulated sheets) — smaller loops → smaller eddy currents → less energy loss.',
        h: 'Eddy currents → energy lost as heat (I²R) in core. Reduced by laminated core (thin sheets).',
        yr: 'SS2',
      },
      {
        q: 'Why must transformers use AC and cannot work on DC?',
        o: [
          'DC has higher voltage than AC',
          'Transformers work by electromagnetic induction — requiring a changing magnetic flux. Steady DC produces constant flux with no change → no induced EMF',
          'DC is too dangerous',
          'AC is cheaper to produce',
        ],
        a: 1,
        e: "Faraday's Law: EMF = −NΔΦ/Δt. DC produces constant flux (ΔΦ/Δt = 0) → no induced EMF in secondary. AC continuously changes → continuously changing flux → continuously induced EMF in secondary. Transformers are AC-only devices.",
        h: 'Transformers need AC: changing flux (ΔΦ/Δt ≠ 0) to induce EMF. DC = constant flux = no induction.',
        yr: 'SS2',
      },
      {
        q: 'The power delivered to the secondary of an ideal transformer compared to the primary is:',
        o: [
          'Greater in the secondary (step-up means more power)',
          'Less in the secondary (step-down means less power)',
          'Equal — in an ideal transformer, V_pI_p = V_sI_s (100% efficiency)',
          'Always zero in secondary',
        ],
        a: 2,
        e: 'Ideal transformer: 100% efficiency. Power in = Power out. V_pI_p = V_sI_s. If voltage steps up (V_s > V_p), current steps down (I_s < I_p) by the same ratio — power is conserved. In practice, efficiency is 95–99% due to eddy currents, hysteresis, and resistance losses.',
        h: 'Ideal transformer: V_pI_p = V_sI_s (power conserved). Higher V → lower I by same ratio.',
        yr: 'SS2',
      },
      {
        q: 'Self-inductance (L) of a coil is a measure of:',
        o: [
          'The resistance of the coil',
          "The coil's ability to oppose changes in the current through it — producing a back-EMF proportional to dI/dt",
          'The number of turns in the coil',
          'The magnetic field strength inside the coil',
        ],
        a: 1,
        e: 'Self-inductance L (henry, H): EMF = −L × dI/dt. When current changes, the changing flux induces a back-EMF that opposes the change. High L → coil strongly opposes sudden current changes. Used in AC circuits to control current (inductor).',
        h: 'Self-inductance L: opposes changes in current. Back-EMF = −LdI/dt. Unit: Henry (H).',
        yr: 'SS2',
      },
      {
        q: 'The use of high voltage for long-distance power transmission reduces energy losses because:',
        o: [
          'High voltage is safer to transmit',
          'At high voltage, current is lower (P = VI, P constant → I = P/V). Lower I → less heat lost in cables (P_loss = I²R)',
          'High voltage cables have lower resistance',
          'High voltage travels faster',
        ],
        a: 1,
        e: 'Power loss in transmission cables: P_loss = I²R. At same power P = VI: higher V → lower I. Lower I reduces heat loss by I² factor. Transmitting 100 kW at 100 kV: I = 1 A; P_loss = R W. At 1 kV: I = 100 A; P_loss = 10,000R W — 10,000 times more! Step-up for transmission; step-down at destination.',
        h: 'High V transmission: lower I → P_loss = I²R much reduced. Step-up for HV; step-down at destination.',
        yr: 'SS2',
      },
      {
        q: 'When a bar magnet is pushed into a solenoid, the end of the solenoid facing the approaching N pole:',
        o: [
          "Becomes a N pole (repelling the approaching magnet — Lenz's Law)",
          'Becomes a S pole (attracting the approaching magnet)',
          'Remains neutral',
          'Produces no current',
        ],
        a: 0,
        e: "Lenz's Law: induced current opposes the flux increase. Approaching N pole increases flux → solenoid end develops N pole (same as approaching pole) → repels the magnet. Work must be done against this repulsion (energy source for the induced current).",
        h: 'Lenz: approaching N pole → solenoid end becomes N pole (repels magnet). Opposes flux increase.',
        yr: 'SS2',
      },
      {
        q: 'The frequency of the AC output of a generator depends on:',
        o: [
          'The strength of the magnets only',
          'The number of coil turns only',
          'The speed of rotation of the coil (and the number of pole pairs) — higher rpm → higher frequency',
          'The resistance of the external circuit',
        ],
        a: 2,
        e: 'Frequency of AC output f = (number of pole pairs) × (rotational speed in rps). For a simple 2-pole generator: f = rotational speed. UK mains = 50 Hz (coil rotates at 50 rps = 3000 rpm). USA = 60 Hz (3600 rpm).',
        h: 'AC generator frequency: f = rotation speed × pole pairs. UK: 50 Hz = 3000 rpm. USA: 60 Hz.',
        yr: 'SS2',
      },
      {
        q: 'A laminated transformer core consists of thin insulated sheets of iron because:',
        o: [
          'Lamination increases magnetic permeability',
          'Lamination reduces eddy current losses — thin sheets restrict eddy current loops to small areas, reducing I²R losses',
          'Lamination makes the core lighter',
          'Lamination increases the EMF produced',
        ],
        a: 1,
        e: 'Laminated core: thin iron sheets separated by thin insulation (varnish). Eddy currents are confined to each thin lamination (small loop area → small eddy current → small I²R loss). Overall core resistance to eddy currents increases greatly, reducing energy loss.',
        h: 'Laminated core: restricts eddy current loops to thin sheets → less I²R energy loss.',
        yr: 'SS2',
      },
      {
        q: 'In an ideal step-down transformer with N_p = 1000 and N_s = 100, if the primary current is 0.5 A, the secondary current is:',
        o: ['0.05 A', '5 A', '50 A', '0.5 A'],
        a: 1,
        e: 'Ideal transformer: V_pI_p = V_sI_s. Also I_s/I_p = N_p/N_s. I_s = I_p × N_p/N_s = 0.5 × 1000/100 = 5 A. Step-down (N_s < N_p) → voltage reduced, current increased.',
        h: 'I_s = I_p × N_p/N_s = 0.5 × 10 = 5 A. Step-down: voltage ↓, current ↑.',
        yr: 'SS2',
      },
      {
        q: "Fleming's Right Hand Rule gives the direction of induced EMF when:",
        o: [
          'Current is applied to a motor',
          'A conductor moves through a magnetic field — the motion (thumb), field (index), and induced EMF (middle finger) indicate directions',
          'Two wires are placed in parallel',
          'A capacitor is charged',
        ],
        a: 1,
        e: "Fleming's Right Hand Rule (generator effect): thuMb = Motion direction; First finger = Field direction; seCond finger = induced Current/EMF direction. This applies whenever mechanical motion in a field produces electricity (generator, dynamo, microphone).",
        h: 'RH Rule: thumb=Motion, index=Field, middle=induced EMF/Current. For generators.',
        yr: 'SS2',
      },
      {
        q: 'Mutual inductance between two coils means:',
        o: [
          'Both coils have equal resistance',
          'A changing current in one coil induces an EMF in the adjacent coil through changing magnetic flux linkage',
          'The coils are connected in parallel',
          'The coils are wound on the same iron core always',
        ],
        a: 1,
        e: 'Mutual inductance M: when current I₁ changes in coil 1, the changing flux links with coil 2, inducing EMF₂ = −M × dI₁/dt. The transformer is based on mutual inductance. M depends on coil geometry, number of turns, and the medium between coils.',
        h: 'Mutual inductance: changing current in one coil induces EMF in adjacent coil. EMF = −M dI/dt.',
        yr: 'SS2',
      },
      {
        q: 'The back-EMF in an electric motor is caused by:',
        o: [
          'The motor drawing too much current',
          "The rotating coil acting as a generator — Lenz's Law — opposing the applied voltage. As motor speeds up, back-EMF increases, reducing net voltage and current",
          'The commutator reversing current',
          'External resistance in the circuit',
        ],
        a: 1,
        e: "A running motor's coil rotates in a magnetic field → acts as a generator → produces back-EMF opposing the supply voltage. Net voltage = V_supply − V_back. As motor accelerates, back-EMF increases → current decreases → power consumption reduces to just what's needed to overcome friction/load.",
        h: 'Motor back-EMF: rotating coil generates opposing voltage (Lenz). Net V = V_supply − V_back.',
        yr: 'SS2',
      },
      {
        q: 'The principle of electromagnetic induction is used in which of the following devices?',
        o: [
          'Simple cell (battery)',
          'Cathode ray oscilloscope',
          'AC generator, transformer, and induction cooktop',
          'Capacitor bank',
        ],
        a: 2,
        e: "Electromagnetic induction (Faraday's Law) underlies: AC generators (mechanical → electrical), transformers (AC voltage stepping), induction cooktops (eddy currents in pot base generate heat), microphones (voice vibrates coil in field), and many more. Batteries use chemical energy; CRO uses electron beams.",
        h: 'Electromagnetic induction: AC generators, transformers, induction cooktops, microphones.',
        yr: 'SS2',
      },
      {
        q: 'If the magnetic flux through a 200-turn coil decreases from 0.05 Wb to 0.01 Wb in 0.2 s, the magnitude of the induced EMF is:',
        o: ['4 V', '40 V', '0.2 V', '200 V'],
        a: 1,
        e: 'EMF = N × ΔΦ/Δt = 200 × (0.05 − 0.01)/0.2 = 200 × 0.04/0.2 = 200 × 0.2 = 40 V.',
        h: 'EMF = NΔΦ/Δt = 200 × 0.04/0.2 = 40 V.',
        yr: 'SS2',
      },
    ],
  },
];
